import { UiButtonDirective } from '@template-ui/directives';
import { requireWorkflowPayloadVersion } from './platform-workflow-definition-lifecycle.models';
import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
  inject,
  signal,
} from '@angular/core';
import { Validators } from '@angular/forms';
import { EzFormComponent } from '@template-ui/components/ez-form/ez-form.component';
import { UiLibraryModule } from '@template-ui/template-ui.module';
import { ButtonConfig, InputSelectOption } from '@template-ui/types';
import { EzFormInputType, InputConfig } from '@template-ui/types/ez-form.types';

import {
  WorkflowAllowedTransition,
  WorkflowDecisionRequirement,
  WorkflowDefinitionDocument,
  WorkflowDefinitionNode,
  WorkflowNotificationRule,
} from './platform-workflow-definition-lifecycle.models';
import { WorkflowRegistryDiagnosticDisplayError } from './platform-workflow-registry-diagnostics.models';
import { WorkflowDraftConfirmationService } from './workflow-draft-confirmation.service';
import { WorkflowNotificationRuleEditorComponent } from './workflow-notification-rule-editor.component';
import type { WorkflowReadOnlyGraphDecisionGateRequest } from './workflow-read-only-graph.models';
import { workflowEditorPendingOwnerScopes, type WorkflowEditorPendingScope, type WorkflowEditorBufferState } from './workflow-editor-pending-state';
import { WorkflowOwnerViewState } from './workflow-owner-view-state';
import type { WorkflowDecisionOwnerRequest } from './workflow-decision-inventory';

type DecisionScopeCode =
  | 'workflow'
  | 'step'
  | 'workflow_transition'
  | 'step_transition';

type ContextualDecisionView = 'summary' | 'rule' | 'notifications';

const TARGET_BEARING_REACTION_CODES = new Set(['branch', 'auto_advance']);
const readableArray = <T>(value: T[] | null | undefined): T[] => Array.isArray(value) ? value : [];
const savedObject = (value: unknown): value is Record<string, unknown> => !!value && typeof value === 'object' && !Array.isArray(value);

interface DecisionLocation {
  scopeCode: DecisionScopeCode;
  stepNodeId: string | null;
  transitionIndex: number | null;
}

interface DecisionSelection extends DecisionLocation {
  decisionIndex: number;
  locationSignature: string;
}

interface DecisionFormValues {
  scopeCode: DecisionScopeCode;
  locationKey: string;
  decisionKind: string;
  requiredOutcomeCode: string;
  reactionCode: string;
  targetStateCode: string;
  eligibleRoleCodes: string[];
  ownerQueueCode: string;
}

export interface WorkflowDraftDecisionRow extends DecisionSelection {
  key: string;
  scopeLabel: string;
  locationLabel: string;
  decision: WorkflowDecisionRequirement | null;
}

export interface WorkflowDraftLegacyDecisionRow {
  key: string;
  locationKey: string;
  scopeCode: 'workflow_transition' | 'step_transition';
  stepNodeId: string | null;
  transitionIndex: number;
  legacyIndex: number;
  scopeLabel: string;
  locationLabel: string;
  decisionKind: string;
}

interface WorkflowDraftDecisionScopeGroup {
  key: string;
  kind: 'workflow' | 'step' | 'evidence';
  stepNodeId: string | null;
  label: string;
  structuredCount: number;
  legacyCount: number;
  authorable: boolean;
}

interface StatusCheckContext {
  scopeCode: 'workflow' | 'step';
  stepNodeId: string | null;
  transitionIndex: number | null;
}

interface WorkflowDraftDecisionNavigatorSection {
  key: string;
  title: string;
  description: string;
  rows: WorkflowDraftDecisionNavigatorRow[];
  separateScope?: boolean;
}

interface WorkflowDraftDecisionNavigatorRow {
  key: string;
  kind: 'structured' | 'legacy';
  scopeKey: string;
  direct: boolean;
  locationLabel: string;
  structured: WorkflowDraftDecisionRow | null;
  legacy: WorkflowDraftLegacyDecisionRow | null;
}

@Component({
  selector: 'phms-platform-workflow-draft-decision-editor',
  imports: [
    UiButtonDirective,
    CommonModule,
    UiLibraryModule,
    WorkflowNotificationRuleEditorComponent,
  ],
  templateUrl: './platform-workflow-draft-decision-editor.component.html',
  styleUrl: './platform-workflow-draft-decision-editor.component.scss',
})
export class PlatformWorkflowDraftDecisionEditorComponent implements OnChanges {
  @Input() contextual = false;
  @Input() statusChecksPresentation = false;
  @Input() inspectionMode = false;
  @Input() document: WorkflowDefinitionDocument | null = null;
  private authoringAllowed = false;
  @Input() set canAuthor(value: boolean) { this.authoringAllowed = value; }
  get canAuthor(): boolean { return this.authoringAllowed && !this.hasOpaqueDecisionCollections; }
  get hasOpaqueDecisionCollections(): boolean {
    const document = this.document;
    if (!document) return false;
    const opaque = (value: unknown) => value != null && !Array.isArray(value);
    const decisionsOpaque = (value: unknown) => opaque(value) || Array.isArray(value) && value.some(decision => savedObject(decision)
      && (opaque(decision['eligibleRoleCodes']) || opaque(decision['notifications'])));
    const transitionsOpaque = (value: unknown) => opaque(value) || Array.isArray(value) && value.some(transition => transition != null
      && (!savedObject(transition) || decisionsOpaque(transition['requiredDecisions']) || opaque(transition['requiredDecisionKinds'])));
    return !Array.isArray(document.nodes) || document.nodes.some(node => !savedObject(node) || typeof node.id !== 'string'
      || decisionsOpaque(node.requiredDecisions) || transitionsOpaque(node.allowedTransitions))
      || decisionsOpaque(document.requiredDecisions) || transitionsOpaque(document.workflowTransitions);
  }
  private readonly opaqueCollectionMessage = 'Some saved decision or transition collections are unavailable. Existing entries remain inspectable; authoring is read-only and the original content is preserved.';
  @Input() interactionBlocked = false;
  @Input() resetContextStepNodeId: string | null | undefined;
  @Input() resetVersion = 0;
  @Input() decisionKindCodes: string[] = [];
  @Input() decisionOutcomeCodes: string[] = [];
  @Input() decisionReactionCodes: string[] = [];
  @Input() stateCodes: string[] = [];
  @Input() roleCodes: string[] = [];
  @Input() queueCodes: string[] = [];
  @Input() managerNotificationTargetCodes: string[] = [];
  @Input() externalNotificationTargetCodes: string[] = [];
  @Input() referenceLoading = false;
  @Input() referenceError: WorkflowRegistryDiagnosticDisplayError | null = null;

  @Output() readonly documentChange =
    new EventEmitter<WorkflowDefinitionDocument>();
  @Output() readonly pendingChangesChange = new EventEmitter<boolean>();
  @Output() readonly retryReferencesRequested = new EventEmitter<void>();
  @Output() readonly workflowChecksRequested = new EventEmitter<void>();
  @Output() readonly notificationContextRequested = new EventEmitter<{
    nodeId: string;
    path: string;
  }>();

  @ViewChild('decisionForm')
  private decisionForm?: EzFormComponent;

  @ViewChild(WorkflowNotificationRuleEditorComponent)
  private notificationEditor?: WorkflowNotificationRuleEditorComponent;

  @ViewChild('decisionEditorHeading')
  private decisionEditorHeading?: ElementRef<HTMLElement>;

  @ViewChild('decisionSectionHeading')
  private decisionSectionHeading?: ElementRef<HTMLElement>;

  @ViewChild('decisionError')
  private decisionError?: ElementRef<HTMLElement>;

  @ViewChild('decisionRuleListHeading')
  private decisionRuleListHeading?: ElementRef<HTMLElement>;

  readonly selectedDecision = signal<DecisionSelection | null>(null);
  readonly selectedLegacyLocationKey = signal<string | null>(null);
  readonly selectedLegacyKey = signal<string | null>(null);
  readonly selectedScopeKey = signal('workflow');
  readonly statusCheckContext = signal<StatusCheckContext | null>(null);
  readonly isAddingDecision = signal(false);
  readonly contextualView = signal<ContextualDecisionView>('summary');
  readonly formDirty = signal(false);
  readonly statusMessage = signal<string | null>(null);
  readonly localError = signal<string | null>(null);
  readonly hasInvalidEligibleRoleEntries = signal(false);

  decisionFormValues: DecisionFormValues = this.emptyFormValues();
  decisionFormConfig: InputConfig[] = [];

  private lastResetVersion: number | null = null;
  private lastResetContextStepNodeId: string | null | undefined;
  private readonly initiallyNullCollections = new Set<string>();
  private selectedEligibleRolesWereNull = true;
  private notificationFormDirty = false;
  private pendingFormBaseline: string | null = null;
  private pendingNotificationBaseline: string | null = null;
  private readonly ownerViews = inject(WorkflowOwnerViewState, { optional: true }) ?? new WorkflowOwnerViewState();
  private readonly destroyRef = inject(DestroyRef);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private localConfirmationInProgress = false;
  private inventoryAddEntry = false;
  private lastOpaqueDecisionCollections = false;

  constructor(
    private readonly confirmation: WorkflowDraftConfirmationService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    const previouslyOpaque = this.lastOpaqueDecisionCollections;
    this.lastOpaqueDecisionCollections = this.hasOpaqueDecisionCollections;
    if (this.lastResetVersion !== this.resetVersion) {
      this.lastResetVersion = this.resetVersion;
      this.lastResetContextStepNodeId = this.resetContextStepNodeId;
      this.captureInitialCollectionShapes();
      this.resetEditor(true);
      this.initializeResetSelection();
      if (this.hasOpaqueDecisionCollections) this.statusMessage.set(this.opaqueCollectionMessage);
      return;
    }

    if (!this.document) {
      this.resetEditor(true);
      return;
    }

    if (changes['document']) {
      this.migrateNullTransitionCollectionShapes(
        changes['document'].previousValue as WorkflowDefinitionDocument | null,
        this.document,
      );
      this.captureNewNullCollectionShapes();
    }

    if (this.lastResetContextStepNodeId !== this.resetContextStepNodeId) {
      const requestedScope =
        this.resetContextStepNodeId === undefined
          ? null
          : this.resetContextStepNodeId === null
            ? 'workflow'
            : this.scopeKeyForNode(this.resetContextStepNodeId);
      if (requestedScope === this.selectedScopeKey()) {
        this.lastResetContextStepNodeId = this.resetContextStepNodeId;
      } else {
        if (this.formDirty() || this.notificationEditorPending) return;
        this.lastResetContextStepNodeId = this.resetContextStepNodeId;
        this.resetEditor(true);
        this.initializeResetSelection();
        return;
      }
    }

    const selection = this.selectedDecision();
    if (selection && !this.resolveSelection(selection)) {
      if (this.formDirty() || this.notificationEditorPending) {
        this.showLocalError('This saved decision location changed. Your input is kept. Reset the form to review the current rules.');
        return;
      }
      this.resetEditor();
      this.initializeSelectionForCurrentScope();
    }

    const legacyKey = this.selectedLegacyKey();
    if (legacyKey && !this.legacyRows.some((row) => row.key === legacyKey)) {
      this.resetEditor();
      this.initializeSelectionForCurrentScope();
    }

    if (changes['document'] && previouslyOpaque !== this.lastOpaqueDecisionCollections && !this.formDirty() && !this.notificationEditorPending) {
      const current = this.selectedRow;
      if (current) this.loadSelection(current);
      else if (this.selectedLegacyRow) this.loadLegacySelection(this.selectedLegacyRow);
    }

    if (
      changes['decisionKindCodes'] ||
      changes['decisionOutcomeCodes'] ||
      changes['decisionReactionCodes'] ||
      changes['stateCodes'] ||
      changes['roleCodes'] ||
      changes['queueCodes'] ||
      changes['canAuthor'] ||
      changes['interactionBlocked'] ||
      changes['contextual'] ||
      changes['statusChecksPresentation']
    ) {
      this.rebuildFormConfig();
    }

    if (
      !this.selectedDecision() &&
      !this.selectedLegacyKey() &&
      !this.isAddingDecision()
    ) {
      this.initializeSelectionForCurrentScope();
    }
    if (this.hasOpaqueDecisionCollections) this.statusMessage.set(this.opaqueCollectionMessage);
  }

  get referenceDataReady(): boolean {
    return this.decisionKindCodes.length > 0;
  }

  get rows(): WorkflowDraftDecisionRow[] {
    const document = this.document;
    if (!document) {
      return [];
    }

    const rows: WorkflowDraftDecisionRow[] = [];
    this.appendRows(
      rows,
      { scopeCode: 'workflow', stepNodeId: null, transitionIndex: null },
      document.requiredDecisions,
    );

    for (const node of this.readableNodes(document)) {
      this.appendRows(
        rows,
        { scopeCode: 'step', stepNodeId: node.id, transitionIndex: null },
        node.requiredDecisions,
      );
      readableArray(node.allowedTransitions).forEach((transition, transitionIndex) => {
        if (!transition) {
          return;
        }
        this.appendRows(
          rows,
          {
            scopeCode: 'step_transition',
            stepNodeId: node.id,
            transitionIndex,
          },
          transition.requiredDecisions,
        );
      });
    }

    readableArray(document.workflowTransitions).forEach(
      (transition, transitionIndex) => {
        if (!transition) {
          return;
        }
        this.appendRows(
          rows,
          {
            scopeCode: 'workflow_transition',
            stepNodeId: null,
            transitionIndex,
          },
          transition.requiredDecisions,
        );
      },
    );

    return rows;
  }

  trackDecisionRow(_index: number, row: WorkflowDraftDecisionRow): string {
    return row.key;
  }

  get legacyRows(): WorkflowDraftLegacyDecisionRow[] {
    const rows: WorkflowDraftLegacyDecisionRow[] = [];
    for (const row of this.transitionLocations) {
      readableArray(row.transition.requiredDecisionKinds).forEach((decisionKind, index) => {
        rows.push({
          key: `${row.locationKey}:legacy:${index}`,
          locationKey: row.locationKey,
          scopeCode:
            row.location.scopeCode === 'workflow_transition'
              ? 'workflow_transition'
              : 'step_transition',
          stepNodeId: row.location.stepNodeId,
          transitionIndex: row.location.transitionIndex!,
          legacyIndex: index,
          scopeLabel: row.scopeLabel,
          locationLabel: row.locationLabel,
          decisionKind,
        });
      });
    }
    return rows;
  }

  trackLegacyDecisionRow(
    _index: number,
    row: WorkflowDraftLegacyDecisionRow,
  ): string {
    return row.key;
  }

  get scopeGroups(): WorkflowDraftDecisionScopeGroup[] {
    const groups: WorkflowDraftDecisionScopeGroup[] = [
      this.buildScopeGroup(
        'workflow',
        'workflow',
        null,
        'Whole workflow',
        true,
      ),
      ...this.humanStepNodes.map((node) =>
        this.buildScopeGroup(
          `step:${node.id}`,
          'step',
          node.id,
          this.nodeFriendlyName(node),
          true,
        ),
      ),
    ];
    const evidence = this.buildScopeGroup(
      'evidence',
      'evidence',
      null,
      this.inspectionMode ? 'Other saved evidence' : 'Other draft evidence',
      false,
    );
    if (evidence.structuredCount + evidence.legacyCount > 0) {
      groups.push(evidence);
    }
    return groups;
  }

  trackScopeGroup(
    _index: number,
    group: WorkflowDraftDecisionScopeGroup,
  ): string {
    return group.key;
  }

  get selectedScopeGroup(): WorkflowDraftDecisionScopeGroup | null {
    return (
      this.scopeGroups.find((group) => group.key === this.selectedScopeKey()) ??
      this.scopeGroups[0] ??
      null
    );
  }

  get visibleRuleRows(): WorkflowDraftDecisionNavigatorRow[] {
    const context = this.statusChecksPresentation
      ? this.statusCheckContext()
      : null;
    const rows = this.navigatorRowsForScope(
      context
        ? context.scopeCode === 'workflow'
          ? 'workflow'
          : this.scopeKeyForNode(context.stepNodeId)
        : this.selectedScopeGroup?.key,
    );
    return context?.scopeCode === 'step'
      ? rows.filter(
          (row) =>
            (row.structured ?? row.legacy)?.stepNodeId === context.stepNodeId,
        )
      : rows;
  }

  private navigatorRowsForScope(
    scopeKey: string | undefined,
  ): WorkflowDraftDecisionNavigatorRow[] {
    if (!scopeKey) {
      return [];
    }

    const result: WorkflowDraftDecisionNavigatorRow[] = [];
    const directRows = this.rows.filter(
      (row) =>
        this.scopeKeyForLocation(row) === scopeKey &&
        (row.scopeCode === 'workflow' || row.scopeCode === 'step'),
    );
    for (const row of directRows) {
      result.push(this.structuredNavigatorRow(row, true));
    }

    for (const transition of this.transitionLocations.filter(
      (row) => this.scopeKeyForLocation(row.location) === scopeKey,
    )) {
      for (const row of this.rows.filter(
        (candidate) =>
          candidate.scopeCode === transition.location.scopeCode &&
          candidate.stepNodeId === transition.location.stepNodeId &&
          candidate.transitionIndex === transition.location.transitionIndex,
      )) {
        result.push(this.structuredNavigatorRow(row, false));
      }
      for (const legacy of this.legacyRows.filter(
        (candidate) => candidate.locationKey === transition.locationKey,
      )) {
        result.push({
          key: legacy.key,
          kind: 'legacy',
          scopeKey,
          direct: false,
          locationLabel: legacy.locationLabel,
          structured: null,
          legacy,
        });
      }
    }
    return result;
  }

  get statusCheckContextLabel(): string | null {
    const context = this.statusChecksPresentation
      ? this.statusCheckContext()
      : null;
    if (!context) return null;
    const owner =
      context.scopeCode === 'workflow'
        ? 'Whole workflow'
        : this.humanStepNodes.find((node) => node.id === context.stepNodeId);
    const ownerLabel =
      typeof owner === 'string'
        ? owner
        : owner
          ? this.nodeFriendlyName(owner)
          : context.stepNodeId || 'Unavailable step';
    const transition = this.statusContextTransition;
    return transition
      ? `${ownerLabel} · ${this.humanizeCode(transition.fromStateCode)} → ${this.humanizeCode(transition.toStateCode)}`
      : ownerLabel;
  }

  get decisionNavigatorSections(): WorkflowDraftDecisionNavigatorSection[] {
    const context = this.statusChecksPresentation
      ? this.statusCheckContext()
      : null;
    const rows = this.visibleRuleRows;
    if (!context) return [{ key: 'rules', title: '', description: '', rows }];
    const ownerLabel =
      context.scopeCode === 'workflow' ? 'the workflow' : 'this step';
    const sections: WorkflowDraftDecisionNavigatorSection[] = [
      {
        key: 'direct',
        title: `Every status change in ${ownerLabel}`,
        description: `These owner-wide checks combine with checks on the requested status change.`,
        rows: rows.filter((row) => row.direct),
      },
    ];
    if (context.transitionIndex !== null) {
      const transition = this.statusContextTransition;
      sections.push({
        key: 'selected-transition',
        title: transition
          ? `Only ${this.humanizeCode(transition.fromStateCode)} → ${this.humanizeCode(transition.toStateCode)}`
          : 'Selected saved status change is unavailable',
        description: this.statusContextTransitionIsDuplicate
          ? 'Retained duplicate configuration. The runtime uses the first saved entry for this status pair.'
          : 'These checks are saved on this status change. They do not replace the owner-wide checks above.',
        rows: rows.filter(
          (row) =>
            !row.direct &&
            (row.structured ?? row.legacy)?.transitionIndex ===
              context.transitionIndex,
        ),
      });
    }
    const otherRows = rows.filter(
      (row) =>
        !row.direct &&
        (row.structured ?? row.legacy)?.transitionIndex !==
          context.transitionIndex,
    );
    if (otherRows.length > 0)
      sections.push({
        key: 'other-transitions',
        title:
          context.transitionIndex === null
            ? 'Checks for specific status changes'
            : 'Checks for other saved status changes',
        description:
          'Each check applies only to its saved status pair. A saved pair does not create a staff action.',
        rows: otherRows,
      });

    return sections;
  }

  get separateWorkflowCheckCount(): number {
    return this.statusChecksPresentation &&
      this.statusCheckContext()?.scopeCode === 'step'
      ? this.navigatorRowsForScope('workflow').length
      : 0;
  }

  reviewWholeWorkflowChecks(): void {
    if (this.interactionBlocked || this.notificationEditorPending) return;
    this.workflowChecksRequested.emit();
  }

  get selectedSavedCheckWarnings(): string[] {
    if (!this.statusChecksPresentation || !this.selectedRow?.decision)
      return [];
    const decision = this.selectedRow.decision;
    const warnings: string[] = [];
    if (!decision.decisionKind?.trim())
      warnings.push(
        'This saved check has no decision type. Inspect or repair the saved entry.',
      );
    if (this.referenceDataReady) {
      if (
        decision.decisionKind?.trim() &&
        !this.decisionKindCodes.includes(decision.decisionKind)
      ) {
        warnings.push(
          `Saved decision type "${decision.decisionKind}" is not among the current authoring choices.`,
        );
      }
      if (
        decision.requiredOutcomeCode &&
        !this.decisionOutcomeCodes.includes(decision.requiredOutcomeCode)
      ) {
        warnings.push(
          `Saved result "${decision.requiredOutcomeCode}" is not among the current authoring choices.`,
        );
      }
      if (
        decision.reaction?.reactionCode &&
        !this.decisionReactionCodes.includes(decision.reaction.reactionCode)
      ) {
        warnings.push(
          `Saved effect "${decision.reaction.reactionCode}" is not among the current authoring choices.`,
        );
      }
    }
    return warnings;
  }

  private get statusContextTransition(): WorkflowAllowedTransition | null {
    const context = this.statusCheckContext();
    return context
      ? this.transitionAt(this.statusContextLocation(context))
      : null;
  }

  private get statusContextTransitionIsDuplicate(): boolean {
    const context = this.statusCheckContext();
    const transition = this.statusContextTransition;
    if (!context || !transition) return false;
    const peers =
      context.scopeCode === 'workflow'
        ? this.document?.workflowTransitions
        : this.readableNodes().find((node) => node.id === context.stepNodeId)
            ?.allowedTransitions;
    return (
      readableArray(peers).findIndex(
        (peer) =>
          peer?.fromStateCode === transition.fromStateCode &&
          peer.toStateCode === transition.toStateCode,
      ) !== context.transitionIndex
    );
  }

  private statusContextLocation(context: StatusCheckContext): DecisionLocation {
    return {
      scopeCode:
        context.scopeCode === 'workflow'
          ? context.transitionIndex === null
            ? 'workflow'
            : 'workflow_transition'
          : context.transitionIndex === null
            ? 'step'
            : 'step_transition',
      stepNodeId: context.scopeCode === 'workflow' ? null : context.stepNodeId,
      transitionIndex: context.transitionIndex,
    };
  }

  private setStatusContextForLocation(location: DecisionLocation | null): void {
    if (!this.statusChecksPresentation) return;
    this.statusCheckContext.set(
      location
        ? {
            scopeCode:
              location.scopeCode === 'workflow' ||
              location.scopeCode === 'workflow_transition'
                ? 'workflow'
                : 'step',
            stepNodeId: location.stepNodeId,
            transitionIndex: location.transitionIndex,
          }
        : null,
    );
  }

  trackNavigatorRow(
    _index: number,
    row: WorkflowDraftDecisionNavigatorRow,
  ): string {
    return row.key;
  }

  get selectedNavigatorKey(): string | null {
    return this.selectedRow?.key ?? this.selectedLegacyKey();
  }

  get selectedLegacyRow(): WorkflowDraftLegacyDecisionRow | null {
    const key = this.selectedLegacyKey();
    return key
      ? (this.legacyRows.find((row) => row.key === key) ?? null)
      : null;
  }

  get visibleStructuredRuleCount(): number {
    return this.visibleRuleRows.filter((row) => row.kind === 'structured')
      .length;
  }

  get availableTargetStateCodes(): string[] {
    const location = this.currentFormLocation();
    return location ? this.targetStateCodesForLocation(location) : [];
  }

  get inspectorTitle(): string {
    if (this.isAddingDecision()) {
      if (this.statusChecksPresentation) return 'Add a decision check';
      return this.contextual
        ? 'Require a recorded decision'
        : 'Add a decision rule';
    }
    const row = this.selectedRow;
    if (row) {
      if (this.contextual && this.contextualView() === 'summary') {
        return row.decision
          ? `${this.decisionKindLabel(row)} ${this.statusChecksPresentation ? 'check' : 'decision'}`
          : this.inspectionMode
            ? 'Incomplete saved decision rule'
            : this.statusChecksPresentation
              ? 'Decision check needs repair'
              : 'Decision rule needs repair';
      }
      if (this.contextual && this.contextualView() === 'notifications') {
        return `${this.decisionKindLabel(row)} notifications`;
      }
      if (this.contextual) {
        if (this.statusChecksPresentation) {
          return row.decision
            ? `${this.inspectionMode ? 'Inspect' : 'Edit'} ${this.decisionKindLabel(row).toLocaleLowerCase()} check`
            : 'Repair incomplete decision check';
        }
        if (this.inspectionMode) {
          return row.decision
            ? `${this.decisionKindLabel(row)} requirement`
            : 'Incomplete saved requirement';
        }
        return row.decision
          ? `Edit ${this.decisionKindLabel(row).toLocaleLowerCase()} requirement`
          : 'Repair incomplete requirement';
      }
      return row.decision
        ? `Edit ${this.decisionKindLabel(row)} rule`
        : 'Repair invalid decision rule';
    }
    const legacy = this.selectedLegacyRow;
    if (legacy) {
      return `${this.humanizeCode(legacy.decisionKind)} compatibility requirement`;
    }
    return this.statusChecksPresentation
      ? 'Choose a decision check'
      : 'Choose a decision rule';
  }

  get inspectorLocationLabel(): string {
    if (this.isAddingDecision()) {
      const location = this.currentFormLocation();
      return location
        ? this.locationLabel(location)
        : (this.selectedScopeGroup?.label ?? '');
    }
    return (
      this.selectedRow?.locationLabel ??
      this.selectedLegacyRow?.locationLabel ??
      this.selectedScopeGroup?.label ??
      ''
    );
  }

  get ruleSummary(): string {
    if (!this.isAddingDecision() && this.selectedRow?.decision === null) {
      if (this.statusChecksPresentation)
        return 'This decision check needs repair.';
      return this.inspectionMode
        ? 'This saved requirement is incomplete.'
        : 'This requirement needs repair.';
    }
    if (!this.decisionFormValues.decisionKind) {
      return this.statusChecksPresentation
        ? 'Choose the recorded decision to check.'
        : 'Choose the required decision.';
    }
    const decisionType = this.humanizeCode(
      this.decisionFormValues.decisionKind,
    );
    const requiredResult = this.decisionFormValues.requiredOutcomeCode
      ? this.humanizeCode(this.decisionFormValues.requiredOutcomeCode)
      : 'Approved';
    const target = this.decisionFormValues.targetStateCode
      ? this.humanizeCode(this.decisionFormValues.targetStateCode)
      : null;
    let summary = `Requires ${decisionType} recorded as ${requiredResult}.`;
    switch (this.decisionFormValues.reactionCode) {
      case '':
      case 'permit':
        break;
      case 'block':
        summary += ` This ${this.statusChecksPresentation ? 'check' : 'rule'} always blocks the status change, even when that result is recorded.`;
        break;
      case 'branch':
        summary += target
          ? ` Destination status: ${target}.`
          : ' The requested status stays the same.';
        break;
      case 'auto_advance':
        summary += target
          ? ` Destination status: ${target}. Work does not move automatically.`
          : ' The requested status stays the same. Work does not move automatically.';
        break;
      default:
        summary += ` Next action: ${this.humanizeCode(
          this.decisionFormValues.reactionCode,
        )}.`;
        break;
    }
    return summary;
  }

  get legacyRuleSummary(): string {
    const kind = this.selectedLegacyRow?.decisionKind?.trim();
    if (!kind) {
      return 'This legacy requirement is incomplete.';
    }
    const label = this.humanizeCode(kind);
    return `Requires ${label} recorded as Approved.`;
  }

  get technicalScopeCode(): string {
    return this.currentFormLocation()?.scopeCode ?? '';
  }

  get technicalLocationCode(): string {
    const location = this.currentFormLocation();
    return location ? this.locationKey(location) : '';
  }

  get technicalStepNodeId(): string | null {
    return this.currentFormLocation()?.stepNodeId ?? null;
  }

  get technicalTransitionIndex(): number | null {
    return this.currentFormLocation()?.transitionIndex ?? null;
  }

  get actionButtons(): ButtonConfig[] {
    const blocked =
      this.interactionBlocked ||
      this.referenceLoading ||
      !this.referenceDataReady;
    return [
      {
        id: 'applyDecision',
        buttonText: this.isAddingDecision()
          ? this.statusChecksPresentation
            ? 'Add decision check'
            : 'Add decision rule'
          : this.selectedRow?.decision === null
            ? 'Replace invalid entry'
            : this.statusChecksPresentation
              ? 'Apply decision check'
              : 'Apply decision rule',
        disabled:
          !this.canAuthor ||
          !this.formDirty() ||
          blocked ||
          this.selectedLocationUnavailable,
        hidden: !this.canAuthor,
      },
      {
        id: 'resetDecision',
        buttonText: this.isAddingDecision()
          ? 'Cancel'
          : this.statusChecksPresentation
            ? 'Reset decision check'
            : 'Reset decision rule',
        severity: 'secondary',
        disabled: blocked,
        hidden: !this.canAuthor,
      },
      {
        id: 'removeDecision',
        buttonText: this.statusChecksPresentation
          ? 'Remove decision check'
          : 'Remove decision rule',
        severity: 'secondary',
        disabled:
          blocked || this.isAddingDecision() || !this.selectedDecision(),
        hidden: !this.canAuthor || this.isAddingDecision(),
      },
    ];
  }

  get selectedKey(): string | null {
    return this.selectedRow?.key ?? null;
  }

  get selectedRow(): WorkflowDraftDecisionRow | null {
    const selection = this.selectedDecision();
    return selection ? this.resolveSelection(selection) : null;
  }

  get selectedLocationUnavailable(): boolean {
    const row = this.selectedRow;
    return row !== null && !this.isAvailableLocation(row);
  }

  get decisionFormVisible(): boolean {
    return (
      this.isAddingDecision() ||
      Boolean(
        this.selectedDecision() &&
        (!this.contextual || this.contextualView() === 'rule'),
      )
    );
  }

  get decisionNotificationsVisible(): boolean {
    return Boolean(
      !this.isAddingDecision() &&
      this.selectedRow?.decision &&
      (this.selectedRow.decision.notifications == null || Array.isArray(this.selectedRow.decision.notifications)) &&
      !this.contextual,
    );
  }

  get contextualSummaryVisible(): boolean {
    return Boolean(
      this.contextual &&
      !this.isAddingDecision() &&
      this.selectedRow &&
      this.contextualView() === 'summary',
    );
  }

  get selectedNotificationSummary(): string {
    const decision = this.selectedRow?.decision;
    if (!decision) {
      return this.inspectionMode
        ? 'Unavailable for this incomplete saved rule'
        : 'Unavailable until the rule is repaired';
    }
    if (decision.notifications != null && !Array.isArray(decision.notifications)) return 'Saved notification rules are unavailable';
    const notifications = readableArray(decision.notifications);
    const configuredCount = notifications.filter(Boolean).length;
    const incompleteCount = notifications.length - configuredCount;
    const configuredLabel =
      configuredCount === 0
        ? 'None'
        : `${configuredCount} rule${configuredCount === 1 ? '' : 's'}`;
    if (incompleteCount === 0) {
      return configuredLabel;
    }
    const incompleteLabel =
      `${incompleteCount} incomplete notification ` +
      `entr${incompleteCount === 1 ? 'y' : 'ies'}`;
    return configuredCount === 0
      ? incompleteLabel
      : `${configuredLabel}; ${incompleteLabel}`;
  }

  get selectedNotificationActionLabel(): string {
    if (
      !this.canAuthor ||
      this.selectedLocationUnavailable ||
      this.referenceLoading ||
      !this.referenceDataReady
    ) {
      return 'View notifications';
    }
    const notifications = this.selectedRow?.decision?.notifications ?? [];
    return notifications.length === 0
      ? 'Add notification'
      : 'Manage notifications';
  }

  get singleRequirementContextLabel(): string | null {
    const row = this.selectedRow;
    if (row)
      return row.scopeCode === 'step' || row.scopeCode === 'workflow'
        ? this.statusChecksPresentation
          ? row.scopeCode === 'workflow'
            ? 'Every workflow status change'
            : 'Every status change in this step'
          : 'Every status change'
        : row.locationLabel;
    return this.selectedLegacyRow
      ? `Read-only · ${this.selectedLegacyRow.locationLabel}`
      : null;
  }

  get graphWorkflow(): boolean {
    return [
      '1.3',
      '1.4',
      '1.5',
      '1.6',
      '1.7',
      '1.8',
      '1.9',
      '1.10',
      '1.11',
      '1.12',
      '1.13',
      '1.14', '1.15', '1.16', '1.17', '1.18', '1.19', '1.20', '1.21', '1.22'].includes(this.document?.payloadSchemaVersion ?? '');
  }

  get unsupportedGraphReaction(): boolean {
    return (
      this.graphWorkflow &&
      this.isTargetBearingReaction(this.decisionFormValues.reactionCode)
    );
  }

  get selectedRolesSummary(): string {
    const decision = this.selectedRow?.decision;
    if (!decision) {
      return this.inspectionMode
        ? 'Unavailable for this incomplete saved rule'
        : 'Unavailable until the rule is repaired';
    }
    if (decision.eligibleRoleCodes != null && !Array.isArray(decision.eligibleRoleCodes)) return 'Saved role guidance is unavailable';
    if (decision.eligibleRoleCodes?.some((code) => code === null)) {
      return 'Contains an invalid role entry';
    }
    return decision.eligibleRoleCodes?.length
      ? decision.eligibleRoleCodes
          .filter((code): code is string => code !== null)
          .map((code) => this.humanizeCode(code))
          .join(', ')
      : 'None';
  }

  get selectedQueueSummary(): string {
    const decision = this.selectedRow?.decision;
    if (!decision) {
      return this.inspectionMode
        ? 'Unavailable for this incomplete saved rule'
        : 'Unavailable until the rule is repaired';
    }
    const queue = decision.ownerQueueCode;
    return queue ? this.humanizeCode(queue) : 'None';
  }

  get notificationEditorPending(): boolean {
    return this.notificationFormDirty;
  }
  private pendingFormSource(document: WorkflowDefinitionDocument | null): string {
    return JSON.stringify([document && [document.workflowCode, document.definitionVersionCode, document.packReference, document.payloadSchemaVersion,
      document.requiredDecisions, document.workflowTransitions, this.readableNodes(document).map(node => [node.id, node.typeCode, node.requiredDecisions, node.allowedTransitions])],
      this.resetVersion, this.selectedDecision()]);
  }
  private pendingDecisionExists(document: WorkflowDefinitionDocument | null): boolean {
    const selection = this.selectedDecision();
    return !!document && !!selection && this.locationSignature(selection, document) === selection.locationSignature &&
      (!selection.stepNodeId || this.readableNodes(document).filter(node => node.id === selection.stepNodeId && node.typeCode === 'human_step').length === 1) &&
      selection.decisionIndex >= 0 && selection.decisionIndex < this.decisionsAt(selection, document).length;
  }
  private pendingDecisionAt(document: WorkflowDefinitionDocument | null): WorkflowDecisionRequirement | null {
    const selection = this.selectedDecision();
    if (!document || !selection || this.locationSignature(selection, document) !== selection.locationSignature) return null;
    if (selection.stepNodeId && this.readableNodes(document).filter(node => node.id === selection.stepNodeId && node.typeCode === 'human_step').length !== 1) return null;
    return this.decisionsAt(selection, document)[selection.decisionIndex] ?? null;
  }
  private pendingNotificationSource(document: WorkflowDefinitionDocument | null): string {
    return JSON.stringify([document && [document.workflowCode, document.definitionVersionCode, document.packReference], this.resetVersion,
      this.selectedDecision(), this.pendingDecisionAt(document)]);
  }
  private pendingFormState(document: WorkflowDefinitionDocument | null): WorkflowEditorBufferState {
    if (!this.formDirty()) return 'clean';
    const location = this.currentFormLocation();
    if (this.inventoryAddEntry && this.isAddingDecision() && !location && document
      && this.pendingFormSource(document) === this.pendingFormBaseline) return 'invalid';
    if (!document || !location || this.pendingFormSource(document) !== this.pendingFormBaseline ||
      (location.stepNodeId && this.readableNodes(document).filter(node => node.id === location.stepNodeId && node.typeCode === 'human_step').length !== 1) ||
      (!this.isAddingDecision() && !this.pendingDecisionExists(document))) return 'stale';
    if (!this.canAuthor || this.interactionBlocked || this.referenceLoading || !this.referenceDataReady ||
      !this.decisionForm?.theFormGroup || this.decisionForm.theFormGroup.invalid || !this.isAvailableLocation(location) || this.hasInvalidEligibleRoleEntries()) return 'invalid';
    const values = this.decisionFormValues, kind = values.decisionKind.trim(), result = normalizeOptional(values.requiredOutcomeCode),
      reaction = normalizeOptional(values.reactionCode), target = reaction ? normalizeOptional(values.targetStateCode) : null,
      queue = normalizeOptional(values.ownerQueueCode);
    if (!this.decisionKindCodes.includes(kind) || (result && !this.decisionOutcomeCodes.includes(result)) ||
      (reaction && !this.decisionReactionCodes.includes(reaction)) || (this.graphWorkflow && this.isTargetBearingReaction(reaction)) ||
      (target && (!this.isTargetBearingReaction(reaction) || !this.stateCodes.includes(target) || !this.targetStateCodesForLocation(location).includes(target))) ||
      values.eligibleRoleCodes.some(code => !this.roleCodes.includes(code.trim())) || (queue && !this.queueCodes.includes(queue))) return 'invalid';
    return 'valid';
  }
  pendingEditorScopes(latestDocument: () => WorkflowDefinitionDocument | null = () => this.document): readonly WorkflowEditorPendingScope[] {
    const notificationOwner = JSON.stringify(this.pendingDecisionAt(this.document));
    const notifications = () => this.pendingDecisionAt(latestDocument());
    const recipientScopes = () => (this.notificationEditor?.pendingEditorScopes(() => notifications()?.notifications ?? null) ?? []).map(scope => ({
      ...scope,
      state: (): WorkflowEditorBufferState => scope.state() === 'clean' ? 'clean' :
        !notifications() || JSON.stringify(notifications()) !== notificationOwner || this.pendingNotificationSource(latestDocument()) !== this.pendingNotificationBaseline ? 'stale' : scope.state(),
      fingerprint: () => JSON.stringify([notificationOwner, this.pendingNotificationBaseline, this.pendingNotificationSource(latestDocument()), notifications(), scope.fingerprint()]),
      apply: () => { const document = latestDocument(); if (!document || !notifications() || JSON.stringify(notifications()) !== notificationOwner || this.pendingNotificationSource(document) !== this.pendingNotificationBaseline) return false; this.document = document; return scope.apply?.() ?? false; },
    }));
    return [
      { id: 'decision-rule-input', label: 'decision rule input', state: () => this.pendingFormState(latestDocument()),
        fingerprint: () => JSON.stringify([this.formDirty(), this.isAddingDecision(), this.decisionFormValues, this.pendingFormBaseline, this.pendingFormSource(latestDocument()),
          this.hasInvalidEligibleRoleEntries(), this.canAuthor, this.interactionBlocked, this.referenceLoading, this.decisionKindCodes, this.decisionOutcomeCodes, this.decisionReactionCodes, this.stateCodes, this.roleCodes, this.queueCodes]),
        apply: () => { const document = latestDocument(); if (!document || this.pendingFormState(document) !== 'valid') return false; this.document = document; this.applyDecision(); return !this.formDirty(); },
        discard: () => {
          const document = latestDocument(); if (document) this.document = document;
          if (!this.formDirty()) return;
          if (this.notificationEditorPending && (this.isAddingDecision() || !this.selectedRow)) {
            this.decisionFormValues = this.emptyFormValues();
            this.hasInvalidEligibleRoleEntries.set(false);
            this.setFormDirty(false);
          } else this.restoreDecisionFormFromDocument();
        },
        focus: () => this.focusAfterRender(() => this.decisionEditorHeading) },
      ...workflowEditorPendingOwnerScopes({ id: 'decision-recipients', owner: () => this.notificationEditor, scopes: recipientScopes,
        aggregatePending: () => this.notificationEditorPending, canApply: () => this.canAuthor && !this.interactionBlocked, focus: scope => scope.focus() }),
    ];
  }

  requestDeactivate(accepted: () => void, cancelled: () => void): void {
    this.requestDiscardPending(accepted, cancelled);
  }

  discardPendingChanges(): void {
    if (this.notificationEditorPending) {
      this.notificationEditor?.discardPendingChanges();
      this.onNotificationPendingChangesChange(false);
    }
    if (this.formDirty()) {
      this.restoreDecisionFormFromDocument();
    }
  }

  focusPendingChanges(): void {
    if (this.notificationEditorPending) {
      this.notificationEditor?.focusPendingChanges();
      return;
    }
    if (this.localError()) {
      this.focusAfterRender(() => this.decisionError);
      return;
    }
    this.focusAfterRender(() => this.decisionEditorHeading);
  }

  selectScope(
    group: WorkflowDraftDecisionScopeGroup,
    moveFocusToRules = true,
  ): void {
    if (
      this.interactionBlocked ||
      this.notificationEditorPending ||
      this.inventoryAddEntry && this.ownerViews.focusSuspended() ||
      group.key === this.selectedScopeKey()
    ) {
      return;
    }

    const inventoryEntry = this.inventoryAddEntry && this.isAddingDecision();
    const select = () => {
      this.resetEditor();
      this.selectedScopeKey.set(group.key);
      if (inventoryEntry && group.authorable && this.canAuthor) this.startInventoryAddForScope(group);
      else this.initializeSelectionForCurrentScope();
      if (moveFocusToRules) {
        this.focusAfterRender(() => inventoryEntry ? this.decisionEditorHeading : this.decisionRuleListHeading);
      }
    };
    const values = this.decisionFormValues;
    if (inventoryEntry && this.document && this.pendingFormSource(this.document) === this.pendingFormBaseline
      && !values.locationKey && !values.decisionKind && !values.requiredOutcomeCode && !values.reactionCode && !values.targetStateCode
      && !values.eligibleRoleCodes.length && !values.ownerQueueCode) select();
    else this.requestDiscardPending(select);
  }

  beginAddDecision(): void {
    if (
      !this.canAuthor ||
      this.interactionBlocked ||
      this.notificationEditorPending ||
      this.referenceLoading ||
      !this.referenceDataReady
    ) {
      return;
    }

    const context = this.statusChecksPresentation
      ? this.statusCheckContext()
      : null;
    const group = context
      ? (this.scopeGroups.find(
          (candidate) =>
            candidate.key ===
            (context.scopeCode === 'workflow'
              ? 'workflow'
              : this.scopeKeyForNode(context.stepNodeId)),
        ) ?? null)
      : this.selectedScopeGroup;
    const location = context
      ? this.statusContextLocation(context)
      : group
        ? (this.authorableLocationsForScope(group)[0] ?? null)
        : null;
    if (
      !group?.authorable ||
      !location ||
      !this.isAvailableLocation(location)
    ) {
      return;
    }

    this.requestDiscardPending(() => {
      this.selectedScopeKey.set(group.key);
      this.startAddDecision(
        location.scopeCode,
        this.locationKey(location),
        true,
        `Add a decision ${this.statusChecksPresentation ? 'check' : 'rule'} for ${group.label}. Choose exactly where it applies, then configure the required result.`,
      );
      this.focusAfterRender(() => this.decisionEditorHeading);
    });
  }

  /** Opens only the exact saved inventory entry. The workspace revalidates its live owner first. */
  focusSavedInventoryCheck(request: WorkflowDecisionOwnerRequest): boolean {
    if (!this.document || request.kind !== 'generic-check' || this.destroyRef.destroyed || this.interactionBlocked
      || this.formDirty() || this.isAddingDecision() || this.notificationEditorPending || this.ownerViews.focusSuspended()) return false;
    const scopes: Record<string, DecisionScopeCode> = { workflow: 'workflow', step: 'step', 'workflow-transition': 'workflow_transition', 'step-transition': 'step_transition' };
    const scopeCode = request.scope ? scopes[request.scope] : undefined;
    if (!scopeCode) return false;
    const step = scopeCode === 'step' || scopeCode === 'step_transition', transition = scopeCode === 'workflow_transition' || scopeCode === 'step_transition';
    if (step ? !request.nodeId || this.readableNodes().filter(node => node.id === request.nodeId).length !== 1 : request.nodeId !== null) return false;
    if (transition ? !Number.isInteger(request.transitionIndex) || request.transitionIndex! < 0 : request.transitionIndex != null) return false;
    const structured = request.decisionIndex != null, legacy = request.legacyIndex != null;
    if (structured === legacy || legacy && !transition || !Number.isInteger(structured ? request.decisionIndex : request.legacyIndex)
      || (structured ? request.decisionIndex! : request.legacyIndex!) < 0) return false;
    const matches = (row: DecisionLocation) => row.scopeCode === scopeCode && row.stepNodeId === (step ? request.nodeId : null)
      && row.transitionIndex === (transition ? request.transitionIndex : null);
    const rows = structured ? this.rows.filter(row => matches(row) && row.decisionIndex === request.decisionIndex)
      : this.legacyRows.filter(row => matches(row) && row.legacyIndex === request.legacyIndex);
    if (rows.length !== 1) return false;
    const row = rows[0], document = this.document, snapshot = JSON.stringify(document);
    this.statusCheckContext.set(this.statusChecksPresentation ? { scopeCode: step ? 'step' : 'workflow', stepNodeId: step ? request.nodeId : null,
      transitionIndex: transition ? request.transitionIndex! : null } : null);
    if (structured) this.loadSelection(row as WorkflowDraftDecisionRow); else this.loadLegacySelection(row as WorkflowDraftLegacyDecisionRow);
    this.focusAfterRender(() => this.document === document && JSON.stringify(document) === snapshot && !this.interactionBlocked
      && (structured ? this.selectedKey === row.key : this.selectedLegacyKey() === row.key) ? this.decisionEditorHeading : undefined);
    return true;
  }

  /** Stages explicit scope, location, type and result choices in the sole existing check editor. */
  beginInventoryCheckAdd(): boolean {
    if (!this.document || !this.canAuthor || this.contextual || !this.statusChecksPresentation || this.destroyRef.destroyed
      || this.interactionBlocked || this.formDirty() || this.isAddingDecision() || this.notificationEditorPending
      || this.referenceLoading || !this.referenceDataReady || this.ownerViews.focusSuspended()) return false;
    this.resetEditor(true);
    this.startInventoryAddForScope(this.selectedScopeGroup!);
    const document = this.document, snapshot = JSON.stringify(document);
    this.focusAfterRender(() => this.document === document && JSON.stringify(document) === snapshot && this.inventoryAddEntry
      && this.isAddingDecision() && !this.interactionBlocked ? this.decisionEditorHeading : undefined);
    return true;
  }

  private startInventoryAddForScope(group: WorkflowDraftDecisionScopeGroup): void {
    this.selectedScopeKey.set(group.key);
    this.startAddDecision(group.kind === 'step' ? 'step_transition' : 'workflow_transition', '', true,
      'Choose the workflow or work-step scope, then select exactly where the check applies, its recorded decision type and required result. No check has been added.', true);
  }

  beginEditDecision(): void {
    if (
      !this.canAuthor ||
      this.interactionBlocked ||
      this.notificationEditorPending ||
      this.referenceLoading ||
      !this.referenceDataReady ||
      !this.selectedRow ||
      this.selectedLocationUnavailable
    ) {
      return;
    }

    this.requestDiscardPending(() => {
      const repairingInvalidEntry = this.selectedRow?.decision === null;
      this.contextualView.set('rule');
      if (repairingInvalidEntry) {
        this.setFormDirty(true);
      }
      this.statusMessage.set(
        repairingInvalidEntry
          ? 'Complete or apply the replacement rule, then save the draft.'
          : null,
      );
      this.focusAfterRender(() => this.decisionEditorHeading);
    });
  }

  openDecisionNotifications(): void {
    if (
      this.interactionBlocked ||
      this.notificationEditorPending ||
      !this.selectedRow?.decision
    ) {
      return;
    }

    if (this.contextual) {
      const selected = this.selectedRow;
      if (!selected?.stepNodeId || !this.applyPendingChanges()) return;
      const current = this.selectedRow;
      if (
        !current?.decision ||
        current.stepNodeId !== selected.stepNodeId ||
        current.scopeCode !== selected.scopeCode ||
        current.transitionIndex !== selected.transitionIndex ||
        current.decisionIndex !== selected.decisionIndex ||
        this.readableNodes().filter((node) => node.id === current.stepNodeId)
          .length !== 1
      )
        return;
      const transitionPath =
        current.transitionIndex === null
          ? ''
          : `.allowedTransitions[${current.transitionIndex}]`;
      this.notificationContextRequested.emit({
        nodeId: current.stepNodeId!,
        path: `$.nodes[id=${current.stepNodeId}]${transitionPath}.requiredDecisions[${current.decisionIndex}]`,
      });
      return;
    }
    this.requestDiscardPending(() => {
      this.contextualView.set('notifications');
      this.statusMessage.set(null);
      this.focusAfterRender(() => this.decisionEditorHeading);
    });
  }

  returnToDecisionSummary(): void {
    if (!this.contextual || this.contextualView() === 'summary') {
      return;
    }

    this.requestDiscardPending(() => {
      this.contextualView.set('summary');
      this.statusMessage.set(null);
      this.focusAfterRender(() => this.decisionEditorHeading);
    });
  }

  reviewDecision(
    row: WorkflowDraftDecisionRow,
    moveFocusToInspector = true,
  ): void {
    if (
      this.interactionBlocked ||
      this.notificationEditorPending ||
      (!this.isAddingDecision() &&
        !this.selectedLegacyKey() &&
        row.key === this.selectedKey)
    ) {
      return;
    }

    this.requestDiscardPending(() => {
      this.selectedScopeKey.set(this.scopeKeyForLocation(row));
      this.loadSelection(row);
      if (moveFocusToInspector) {
        this.focusAfterRender(() => this.decisionEditorHeading);
      }
    });
  }

  reviewLegacyDecision(
    row: WorkflowDraftLegacyDecisionRow,
    moveFocusToInspector = true,
  ): void {
    if (
      this.interactionBlocked ||
      this.notificationEditorPending ||
      (!this.isAddingDecision() &&
        !this.selectedDecision() &&
        row.key === this.selectedLegacyKey())
    ) {
      return;
    }

    this.requestDiscardPending(() => {
      this.loadLegacySelection(row);
      if (moveFocusToInspector) {
        this.focusAfterRender(() => this.decisionEditorHeading);
      }
    });
  }

  reviewNavigatorRow(
    row: WorkflowDraftDecisionNavigatorRow,
    moveFocusToInspector = true,
  ): void {
    if (row.structured) {
      this.reviewDecision(row.structured, moveFocusToInspector);
    } else if (row.legacy) {
      this.reviewLegacyDecision(row.legacy, moveFocusToInspector);
    }
  }

  navigatorAriaLabel(row: WorkflowDraftDecisionNavigatorRow): string {
    if (row.structured) {
      return this.decisionAriaLabel(row.structured);
    }
    return row.legacy
      ? `Inspect read-only ${this.humanizeCode(row.legacy.decisionKind)} compatibility requirement at ${row.locationLabel}`
      : this.statusChecksPresentation
        ? 'Inspect decision check'
        : 'Inspect decision rule';
  }

  navigatorRequirementLabel(row: WorkflowDraftDecisionNavigatorRow): string {
    if (row.structured) {
      return row.structured.decision?.decisionKind?.trim()
        ? `${this.decisionKindLabel(row.structured)} ${this.statusChecksPresentation ? 'check' : 'required'}`
        : this.statusChecksPresentation
          ? 'Incomplete check'
          : 'Incomplete requirement';
    }
    return row.legacy
      ? `${this.humanizeCode(row.legacy.decisionKind)} compatibility ${this.statusChecksPresentation ? 'check' : 'requirement'}`
      : this.statusChecksPresentation
        ? 'Decision check'
        : 'Decision requirement';
  }

  navigatorContextLabel(row: WorkflowDraftDecisionNavigatorRow): string {
    const location = row.direct
      ? this.statusChecksPresentation
        ? row.scopeKey === 'workflow'
          ? 'Every workflow status change'
          : 'Every status change in this step'
        : 'Every status change'
      : row.locationLabel;
    return row.kind === 'legacy' ? `Read-only · ${location}` : location;
  }

  navigatorExceptionLabel(
    row: WorkflowDraftDecisionNavigatorRow,
  ): string | null {
    const decision = row.structured?.decision;
    if (!decision) {
      return null;
    }
    const details: string[] = [];
    const decisionKind = decision.decisionKind?.trim() ?? '';
    const requiredOutcome = decision.requiredOutcomeCode?.trim();
    const effectiveOutcome = requiredOutcome || 'approved';
    if (
      decisionKind &&
      (effectiveOutcome.toLocaleLowerCase() !== 'approved' ||
        decisionKind.toLocaleLowerCase() !== 'approval')
    ) {
      details.push(`Must be ${this.humanizeCode(effectiveOutcome)}`);
    }
    const reactionCode = decision.reaction?.reactionCode ?? '';
    const target = decision.reaction?.targetStateCode;
    switch (reactionCode) {
      case '':
      case 'permit':
        break;
      case 'block':
        details.push('Always blocks, even with the required result');
        break;
      case 'branch':
        details.push(
          target
            ? `Destination status: ${this.humanizeCode(target)}`
            : 'Keeps the requested status',
        );
        break;
      case 'auto_advance':
        details.push(
          target
            ? `Destination status: ${this.humanizeCode(target)}; does not move automatically`
            : 'Does not move automatically',
        );
        break;
      default:
        details.push(`Next: ${this.humanizeCode(reactionCode)}`);
        break;
    }
    return details.length > 0 ? details.join(' · ') : null;
  }

  onFormChange(values: unknown): void {
    if (
      !this.canAuthor ||
      this.interactionBlocked ||
      this.referenceLoading ||
      !this.referenceDataReady ||
      this.selectedLocationUnavailable
    ) {
      return;
    }

    const incoming = values as Partial<DecisionFormValues>;
    const locationKey =
      incoming.locationKey ?? this.decisionFormValues.locationKey;
    const parsedLocation = this.isAddingDecision()
      ? this.parseAnyLocation(locationKey)
      : this.selectedDecision();
    const scopeCode =
      parsedLocation?.scopeCode ?? this.decisionFormValues.scopeCode;
    const locationChanged =
      locationKey !== this.decisionFormValues.locationKey ||
      scopeCode !== this.decisionFormValues.scopeCode;
    const reactionCode =
      incoming.reactionCode ?? this.decisionFormValues.reactionCode;
    const reactionChanged =
      reactionCode !== this.decisionFormValues.reactionCode;
    const targetStateCode =
      !reactionCode ||
      (reactionChanged && !this.isTargetBearingReaction(reactionCode))
        ? ''
        : (incoming.targetStateCode ?? this.decisionFormValues.targetStateCode);
    const availableTargets = parsedLocation
      ? this.targetStateCodesForLocation(parsedLocation)
      : [];
    const nextTargetStateCode =
      locationChanged &&
      targetStateCode &&
      !availableTargets.includes(targetStateCode)
        ? ''
        : targetStateCode;
    this.decisionFormValues = {
      scopeCode,
      locationKey,
      decisionKind:
        incoming.decisionKind ?? this.decisionFormValues.decisionKind,
      requiredOutcomeCode:
        incoming.requiredOutcomeCode ??
        this.decisionFormValues.requiredOutcomeCode,
      reactionCode,
      targetStateCode: nextTargetStateCode,
      eligibleRoleCodes: Array.isArray(incoming.eligibleRoleCodes)
        ? incoming.eligibleRoleCodes
        : this.decisionFormValues.eligibleRoleCodes,
      ownerQueueCode:
        incoming.ownerQueueCode ?? this.decisionFormValues.ownerQueueCode,
    };
    if (locationChanged || reactionChanged) {
      this.decisionFormConfig = this.buildFormConfig(
        this.isAddingDecision(),
        this.selectedRow?.decision ?? undefined,
      );
    }
    this.setFormDirty(true);
    this.localError.set(null);
  }

  onActionButtonClick(button: ButtonConfig): void {
    switch (button.id) {
      case 'applyDecision':
        this.applyDecision();
        break;
      case 'resetDecision':
        this.resetDecision();
        break;
      case 'removeDecision':
        this.requestRemoveDecision();
        break;
      default:
        break;
    }
  }

  applyPendingChanges(latestDocument?: WorkflowDefinitionDocument): boolean {
    if (!this.formDirty() && !this.notificationEditorPending) return true;
    if (!this.canAuthor || this.interactionBlocked) return false;
    if (this.notificationEditorPending && this.pendingNotificationSource(latestDocument ?? this.document) !== this.pendingNotificationBaseline) {
      this.showLocalError('This notification owner changed while editing. Your recipient input is kept.');
      return false;
    }
    if (this.formDirty() && this.pendingFormState(latestDocument ?? this.document) === 'stale') {
      this.showLocalError('This decision rule source changed while editing. Your input is kept.');
      return false;
    }
    if (latestDocument) this.document = latestDocument;
    const location = this.isAddingDecision()
      ? this.parseLocation(
          this.decisionFormValues.scopeCode,
          this.decisionFormValues.locationKey,
        )
      : this.selectedDecision();
    if (
      !this.document ||
      !location ||
      !this.isAvailableLocation(location) ||
      (!this.isAddingDecision() && !this.selectedRow)
    ) {
      this.showLocalError(
        'This decision-rule location is no longer available. Your edits remain open.',
      );
      return false;
    }
    const capture = this.documentChange.subscribe(
      (value) => (this.document = value),
    );
    try {
      if (this.formDirty()) this.applyDecision();
      if (this.formDirty()) return false;
      if (this.notificationEditorPending) {
        if (!this.notificationEditor || !this.selectedRow?.decision)
          return false;
        this.notificationEditor.interactionBlocked = this.interactionBlocked;
        if (
          !this.notificationEditor.applyPendingChanges(
            this.selectedRow.decision.notifications ?? null,
          )
        )
          return false;
      }
      return !this.notificationEditorPending;
    } finally {
      capture.unsubscribe();
    }
  }

  applyDecision(): void {
    if (this.formDirty() && this.pendingFormState(this.document) === 'stale') {
      this.showLocalError('This decision rule source changed while editing. Your input is kept.');
      return;
    }
    if (
      !this.document ||
      !this.canAuthor ||
      this.interactionBlocked ||
      this.referenceLoading ||
      !this.referenceDataReady ||
      !this.ensureFormValid()
    ) {
      return;
    }

    const values = this.decisionFormValues;
    const location = this.isAddingDecision()
      ? this.parseLocation(values.scopeCode, values.locationKey)
      : this.selectedDecision();
    if (!location || !this.isAvailableLocation(location)) {
      this.showLocalError(
        'Choose an available workflow, human-step, or transition location.',
      );
      return;
    }

    const decisionKind = values.decisionKind.trim();
    const requiredOutcomeCode = normalizeOptional(values.requiredOutcomeCode);
    const reactionCode = normalizeOptional(values.reactionCode);
    const targetStateCode = reactionCode
      ? normalizeOptional(values.targetStateCode)
      : null;
    if (this.hasInvalidEligibleRoleEntries()) {
      this.showLocalError(
        'Remove the invalid null eligible-role entry before applying this rule.',
      );
      return;
    }
    const eligibleRoleCodes = values.eligibleRoleCodes.map((code) =>
      code.trim(),
    );
    const ownerQueueCode = normalizeOptional(values.ownerQueueCode);

    if (!this.decisionKindCodes.includes(decisionKind)) {
      this.showLocalError(
        'Choose an active decision kind from the authoring catalog.',
      );
      return;
    }
    if (
      requiredOutcomeCode &&
      !this.decisionOutcomeCodes.includes(requiredOutcomeCode)
    ) {
      this.showLocalError(
        'Choose an active required outcome from the authoring catalog.',
      );
      return;
    }
    if (reactionCode && !this.decisionReactionCodes.includes(reactionCode)) {
      this.showLocalError(
        'Choose an active reaction from the authoring catalog.',
      );
      return;
    }
    if (this.graphWorkflow && this.isTargetBearingReaction(reactionCode)) {
      this.showLocalError(
        'This saved status-redirection effect is unsupported in graph workflows. Choose an allowed effect; configure destinations in Outcomes & routes.',
      );
      return;
    }
    if (targetStateCode && !this.isTargetBearingReaction(reactionCode)) {
      this.showLocalError(
        'Clear the destination or choose a redirect action before applying this rule.',
      );
      return;
    }
    if (targetStateCode && !this.stateCodes.includes(targetStateCode)) {
      this.showLocalError(
        'Choose an active reaction target state from the authoring catalog.',
      );
      return;
    }
    if (
      targetStateCode &&
      !this.targetStateCodesForLocation(location).includes(targetStateCode)
    ) {
      this.showLocalError(
        'Choose a destination that is configured from every status where this rule applies.',
      );
      return;
    }
    if (eligibleRoleCodes.some((code) => !this.roleCodes.includes(code))) {
      this.showLocalError(
        'Choose only active eligible roles from the authoring catalog.',
      );
      return;
    }
    if (ownerQueueCode && !this.queueCodes.includes(ownerQueueCode)) {
      this.showLocalError(
        'Choose an active owner queue from the authoring catalog.',
      );
      return;
    }

    const existingNotifications = this.selectedRow?.decision?.notifications;
    const nextDecision: WorkflowDecisionRequirement = {
      ...this.selectedRow?.decision,
      decisionKind,
      requiredOutcomeCode,
      reaction: reactionCode
        ? {
            reactionCode,
            targetStateCode,
          }
        : null,
      eligibleRoleCodes:
        eligibleRoleCodes.length > 0
          ? eligibleRoleCodes
          : this.selectedEligibleRolesWereNull
            ? null
            : [],
      ownerQueueCode,
      ...(existingNotifications === undefined
        ? {}
        : { notifications: existingNotifications }),
    };
    const decisions = this.decisionsAt(location);
    const selection = this.selectedDecision();
    const nextDecisionIndex = selection?.decisionIndex ?? decisions.length;
    const nextDecisions = selection
      ? decisions.map((decision, index) =>
          index === selection.decisionIndex ? nextDecision : decision,
        )
      : [...decisions, nextDecision];
    const nextDocument = this.replaceDecisions(location, nextDecisions);
    const nextSignature = this.locationSignature(location, nextDocument);

    this.inventoryAddEntry = false;
    this.isAddingDecision.set(false);
    this.contextualView.set('summary');
    this.selectedLegacyKey.set(null);
    this.selectedLegacyLocationKey.set(null);
    this.selectedScopeKey.set(this.scopeKeyForLocation(location));
    this.selectedDecision.set({
      ...location,
      decisionIndex: nextDecisionIndex,
      locationSignature: nextSignature,
    });
    this.selectedEligibleRolesWereNull =
      nextDecision.eligibleRoleCodes === null;
    this.hasInvalidEligibleRoleEntries.set(false);
    this.decisionFormValues = this.toFormValues(location, nextDecision);
    this.decisionFormConfig = this.buildFormConfig(false, nextDecision);
    this.setFormDirty(false);
    this.localError.set(null);
    this.statusMessage.set(
      selection
        ? this.statusChecksPresentation
          ? 'Decision check changes applied to the working draft.'
          : 'Decision rule changes applied to the working draft.'
        : this.statusChecksPresentation
          ? 'Decision check added to the working draft.'
          : 'Decision rule added to the working draft.',
    );
    this.documentChange.emit(nextDocument);
    if (this.contextual) {
      this.focusAfterRender(() => this.decisionEditorHeading);
    }
  }

  resetDecision(): void {
    if (this.isAddingDecision()) {
      this.resetEditor();
      this.initializeSelectionForCurrentScope();
      this.statusMessage.set(
        this.statusChecksPresentation
          ? 'New decision check cancelled.'
          : 'New decision rule cancelled.',
      );
      this.focusAfterRender(() =>
        this.selectedNavigatorKey
          ? this.decisionEditorHeading
          : this.decisionRuleListHeading,
      );
      return;
    }

    const row = this.selectedRow;
    if (!row) {
      this.resetEditor();
      return;
    }

    if (this.contextual) {
      this.loadSelection(row);
      this.contextualView.set('summary');
      this.statusMessage.set(
        this.statusChecksPresentation
          ? 'Unapplied decision check changes cancelled.'
          : 'Unapplied decision rule changes cancelled.',
      );
      this.focusAfterRender(() => this.decisionEditorHeading);
      return;
    }

    this.loadSelection(row);
    this.statusMessage.set(
      this.statusChecksPresentation
        ? 'Unapplied decision check changes reset.'
        : 'Unapplied decision rule changes reset.',
    );
    this.focusAfterRender(() => this.decisionEditorHeading);
  }

  applyDecisionNotifications(
    notifications: Array<WorkflowNotificationRule | null> | null,
  ): void {
    const selection = this.selectedDecision();
    const row = this.selectedRow;
    if (!selection || !row?.decision || !this.document || !this.canAuthor) {
      return;
    }
    const decisions = this.decisionsAt(row);
    let nextDocument = this.replaceDecisions(
      row,
      decisions.map((decision, index) =>
        index === row.decisionIndex && decision
          ? { ...decision, notifications }
          : decision,
      ),
    );
    if ((notifications?.length ?? 0) > 0) {
      nextDocument = {
        ...nextDocument,
        payloadSchemaVersion: requireWorkflowPayloadVersion(
          nextDocument.payloadSchemaVersion,
          '1.1',
        ),
      };
    }
    this.selectedDecision.set({
      ...selection,
      locationSignature: this.locationSignature(row, nextDocument),
    });
    this.statusMessage.set(
      (notifications?.filter(Boolean).length ?? 0) > 0
        ? 'Notification recipients updated in the working draft.'
        : 'Notifications removed from this decision rule in the working draft.',
    );
    this.documentChange.emit(nextDocument);
  }

  onNotificationPendingChangesChange(value: boolean): void {
    if (this.notificationFormDirty === value) {
      return;
    }
    this.pendingNotificationBaseline = value ? this.pendingNotificationSource(this.document) : null;
    this.notificationFormDirty = value;
    this.pendingChangesChange.emit(
      this.formDirty() || this.notificationFormDirty,
    );
  }

  removeInvalidEligibleRoleEntries(): void {
    if (
      !this.hasInvalidEligibleRoleEntries() ||
      !this.canAuthor ||
      this.interactionBlocked ||
      this.referenceLoading ||
      this.selectedLocationUnavailable
    ) {
      return;
    }

    this.hasInvalidEligibleRoleEntries.set(false);
    this.setFormDirty(true);
    this.localError.set(null);
    this.statusMessage.set(
      'The invalid null eligible-role entry will be removed when this rule is applied.',
    );
  }

  focusDecisionPath(
    path: string,
    locationId: string | null,
    source: 'validation' | 'review' = 'validation',
  ): void {
    if (source === 'review') {
      this.focusReviewedDecisionPath(path, locationId);
      return;
    }

    const scope = path.includes('.allowedTransitions')
      ? 'step_transition'
      : path.includes('workflowTransitions')
        ? 'workflow_transition'
        : path.includes('.nodes') || locationId
          ? 'step'
          : 'workflow';
    this.requestDiscardPending(() => {
      const row = this.rows.find(
        (candidate) =>
          candidate.scopeCode === scope &&
          ((scope !== 'step' && scope !== 'step_transition') ||
            candidate.stepNodeId === locationId),
      );
      if (row) {
        this.setStatusContextForLocation(row);
        this.selectedScopeKey.set(this.scopeKeyForLocation(row));
        this.loadSelection(row);
      } else {
        this.selectedScopeKey.set(
          scope === 'workflow' || scope === 'workflow_transition'
            ? 'workflow'
            : this.scopeKeyForNode(locationId),
        );
      }
      this.statusMessage.set(
        row
          ? `Validation identified a decision rule issue at ${row.locationLabel}.`
          : 'Validation identified a decision rule issue in this section.',
      );
      this.focusAfterRender(() =>
        row ? this.decisionEditorHeading : this.decisionSectionHeading,
      );
    });
  }

  private focusReviewedDecisionPath(
    path: string,
    locationId: string | null,
  ): void {
    const location = this.persistedDecisionLocation(path, locationId);
    const requestedScopeKey =
      location && this.reviewLocationAvailable(location)
        ? this.scopeKeyForLocation(location)
        : null;
    const requestedGroup = requestedScopeKey
      ? (this.scopeGroups.find((group) => group.key === requestedScopeKey) ??
        null)
      : null;
    const fallbackGroup = requestedGroup ?? this.scopeGroups[0] ?? null;

    this.requestDiscardPending(() => {
      const row = location ? this.reviewedDecisionRow(path, location) : null;
      this.resetEditor();
      this.setStatusContextForLocation(location);
      this.selectedScopeKey.set(fallbackGroup?.key ?? 'workflow');
      if (row) {
        this.loadSelection(row);
      }
      this.statusMessage.set(
        row
          ? `Showing saved decision-rule behavior at ${row.locationLabel}.`
          : requestedGroup
            ? `That saved decision rule is no longer present. Showing decision rules for ${requestedGroup.label}.`
            : 'That saved decision rule belongs to a draft element that is no longer available. Showing the decision-rule section.',
      );
      this.focusAfterRender(() =>
        row
          ? this.decisionEditorHeading
          : requestedGroup
            ? this.decisionRuleListHeading
            : this.decisionSectionHeading,
      );
    });
  }

  private persistedDecisionLocation(
    path: string,
    locationId: string | null,
  ): DecisionLocation | null {
    const stepNodeId = locationId ?? stepNodeIdFromPersistedDecisionPath(path);
    if (path.includes('.allowedTransitions')) {
      return {
        scopeCode: 'step_transition',
        stepNodeId,
        transitionIndex: this.persistedTransitionIndex(
          path,
          'step',
          stepNodeId,
        ),
      };
    }
    if (path.includes('workflowTransitions')) {
      return {
        scopeCode: 'workflow_transition',
        stepNodeId: null,
        transitionIndex: this.persistedTransitionIndex(path, 'workflow', null),
      };
    }
    if (path.includes('.nodes')) {
      return {
        scopeCode: 'step',
        stepNodeId,
        transitionIndex: null,
      };
    }
    return {
      scopeCode: 'workflow',
      stepNodeId: null,
      transitionIndex: null,
    };
  }

  private persistedTransitionIndex(
    path: string,
    scopeCode: 'workflow' | 'step',
    stepNodeId: string | null,
  ): number | null {
    const selector = persistedTransitionSelector(path);
    if (!selector) {
      return null;
    }
    if (selector.index !== null) {
      return selector.index;
    }

    const transitions =
      scopeCode === 'workflow'
        ? readableArray(this.document?.workflowTransitions)
        : readableArray(this.readableNodes().find((node) => node.id === stepNodeId)?.allowedTransitions);
    const index = transitions.findIndex(
      (transition) =>
        transition?.fromStateCode === selector.fromStateCode &&
        transition.toStateCode === selector.toStateCode,
    );
    return index >= 0 ? index : null;
  }

  private reviewedDecisionRow(
    path: string,
    location: DecisionLocation,
  ): WorkflowDraftDecisionRow | null {
    const selector = persistedDecisionSelector(path);
    if (!selector) {
      return null;
    }

    const rows = this.rows.filter(
      (row) =>
        row.scopeCode === location.scopeCode &&
        row.stepNodeId === location.stepNodeId &&
        row.transitionIndex === location.transitionIndex,
    );
    if (selector.index !== null) {
      return rows.find((row) => row.decisionIndex === selector.index) ?? null;
    }

    const matchingRows = rows
      .filter((row) => row.decision !== null)
      .filter((row) => {
        const decisionKind =
          row.decision?.decisionKind?.trim() || '(missing-key)';
        return decisionKind === selector.decisionKind;
      })
      .sort((left, right) => {
        const leftDescription = this.semanticDecisionDescription(
          left.decision!,
        );
        const rightDescription = this.semanticDecisionDescription(
          right.decision!,
        );
        if (leftDescription < rightDescription) {
          return -1;
        }
        if (leftDescription > rightDescription) {
          return 1;
        }
        return left.decisionIndex - right.decisionIndex;
      });
    return matchingRows[selector.occurrence - 1] ?? null;
  }

  private semanticDecisionDescription(
    decision: WorkflowDecisionRequirement,
  ): string {
    const roles = [
      ...new Set(
        readableArray(decision.eligibleRoleCodes).filter(
          (code): code is string => code !== null,
        ),
      ),
    ].sort();
    const valueOrNone = (value: string | null | undefined): string =>
      value ?? '(none)';
    return [
      `kind=${decision.decisionKind ?? ''}`,
      `outcome=${valueOrNone(decision.requiredOutcomeCode)}`,
      `reaction=${valueOrNone(decision.reaction?.reactionCode)}`,
      `target=${valueOrNone(decision.reaction?.targetStateCode)}`,
      `roles=${valueOrNone(roles.length > 0 ? roles.join(', ') : null)}`,
      `queue=${valueOrNone(decision.ownerQueueCode)}`,
    ].join('; ');
  }

  private reviewLocationAvailable(location: DecisionLocation): boolean {
    if (
      location.scopeCode === 'workflow' ||
      location.scopeCode === 'workflow_transition'
    ) {
      return true;
    }
    return (
      this.readableNodes().some((node) => node.id === location.stepNodeId) ??
      false
    );
  }

  focusStepDecisions(nodeId: string): void {
    if (this.statusChecksPresentation) {
      this.focusStatusChecks({
        scopeCode: 'step',
        stepNodeId: nodeId,
        transitionIndex: null,
      });
      return;
    }
    this.requestDiscardPending(() => {
      const row = this.rows.find(
        (candidate) =>
          (candidate.scopeCode === 'step' ||
            candidate.scopeCode === 'step_transition') &&
          candidate.stepNodeId === nodeId,
      );
      const step = this.humanStepNodes.find((node) => node.id === nodeId);
      const stepLabel = step?.displayName || step?.stepCode || nodeId;
      this.selectedScopeKey.set(step ? `step:${nodeId}` : 'evidence');
      let focusEditor = false;
      if (row) {
        this.loadSelection(row);
        this.statusMessage.set(
          this.contextual ? null : `Showing a decision rule for ${stepLabel}.`,
        );
        focusEditor = true;
      } else if (
        step &&
        this.canAuthor &&
        !this.interactionBlocked &&
        !this.referenceLoading &&
        this.referenceDataReady
      ) {
        this.startAddDecision(
          'step',
          this.locationKey({
            scopeCode: 'step',
            stepNodeId: nodeId,
            transitionIndex: null,
          }),
          true,
          `Add a decision ${this.statusChecksPresentation ? 'check' : 'rule'} for ${stepLabel}. The work-step location is already selected.`,
        );
        focusEditor = true;
      } else {
        this.resetEditor();
        this.statusMessage.set(
          `${stepLabel} has no step-specific decision rules. Decision-rule authoring is not available right now.`,
        );
      }
      this.focusAfterRender(() =>
        focusEditor ? this.decisionEditorHeading : this.decisionSectionHeading,
      );
    });
  }

  focusDecisionOverview(stepNodeId: string | null): void {
    if (this.statusChecksPresentation) {
      this.focusStatusChecks({
        scopeCode: stepNodeId === null ? 'workflow' : 'step',
        stepNodeId,
        transitionIndex: null,
      });
      return;
    }
    const requestedScopeKey =
      stepNodeId === null ? 'workflow' : this.scopeKeyForNode(stepNodeId);
    this.requestDiscardPending(() => {
      const requestedGroup =
        this.scopeGroups.find((group) => group.key === requestedScopeKey) ??
        null;
      const structured = requestedGroup
        ? (this.rows.find(
            (row) => this.scopeKeyForLocation(row) === requestedScopeKey,
          ) ?? null)
        : null;
      const legacy = requestedGroup
        ? (this.legacyRows.find(
            (row) => this.scopeKeyForLocation(row) === requestedScopeKey,
          ) ?? null)
        : null;

      this.resetEditor();
      this.selectedScopeKey.set(requestedGroup?.key ?? 'workflow');
      if (structured) {
        this.loadSelection(structured);
      } else if (legacy) {
        this.loadLegacySelection(legacy);
      }
      this.statusMessage.set(
        requestedGroup
          ? null
          : 'That workflow step is no longer available. Showing workflow decision requirements.',
      );
      this.focusAfterRender(() =>
        structured || legacy
          ? this.decisionEditorHeading
          : this.decisionRuleListHeading,
      );
    });
  }

  focusDecisionGate(request: WorkflowReadOnlyGraphDecisionGateRequest): void {
    const location = this.decisionGateLocation(request);
    const requestedScopeKey = location
      ? this.scopeKeyForLocation(location)
      : request.ownerSourceNodeId === null
        ? 'workflow'
        : 'evidence';

    this.requestDiscardPending(() => {
      this.resetEditor();
      this.setStatusContextForLocation(location);
      this.selectedScopeKey.set(
        this.scopeGroups.some((group) => group.key === requestedScopeKey)
          ? requestedScopeKey
          : 'workflow',
      );

      const decisionIndex = request.origin.decisionIndex;
      const structured =
        location &&
        request.origin.sourceKind === 'structured' &&
        decisionIndex !== null
          ? (this.rows.find(
              (row) =>
                row.scopeCode === location.scopeCode &&
                row.stepNodeId === location.stepNodeId &&
                row.transitionIndex === location.transitionIndex &&
                row.decisionIndex === decisionIndex,
            ) ?? null)
          : null;
      const legacy =
        location &&
        request.origin.sourceKind === 'legacy' &&
        decisionIndex !== null
          ? (this.legacyRows.find(
              (row) =>
                row.scopeCode === location.scopeCode &&
                row.stepNodeId === location.stepNodeId &&
                row.transitionIndex === location.transitionIndex &&
                row.legacyIndex === decisionIndex,
            ) ?? null)
          : null;

      if (structured) {
        this.loadSelection(structured);
      } else if (legacy) {
        this.loadLegacySelection(legacy);
      }

      const selectedLocation =
        structured?.locationLabel ?? legacy?.locationLabel ?? null;
      if (selectedLocation) {
        this.statusMessage.set(
          request.representsGroupedOrigins
            ? `This decision gate represents ${request.authoredOriginCount} equivalent saved requirements. Showing the first saved requirement in document order at ${selectedLocation}.`
            : legacy
              ? `Showing the preserved legacy requirement at ${selectedLocation}. It remains read-only compatibility evidence.`
              : `Showing the saved decision requirement at ${selectedLocation}.`,
        );
      } else {
        this.statusMessage.set(
          decisionIndex === null
            ? 'This gate points to malformed saved decision evidence that cannot be opened as an individual rule. No new rule was started.'
            : 'The saved decision requirement represented by this gate is no longer present. No new rule was started.',
        );
      }

      this.focusAfterRender(() =>
        structured || legacy
          ? this.decisionEditorHeading
          : (this.decisionRuleListHeading ?? this.decisionSectionHeading),
      );
    });
  }

  focusTransitionDecisions(request: {
    scopeCode: 'workflow' | 'step';
    stepNodeId: string | null;
    transitionIndex: number;
  }): void {
    if (this.statusChecksPresentation) {
      this.focusStatusChecks(request);
      return;
    }
    this.requestDiscardPending(() => {
      const location: DecisionLocation = {
        scopeCode:
          request.scopeCode === 'workflow'
            ? 'workflow_transition'
            : 'step_transition',
        stepNodeId:
          request.scopeCode === 'workflow' ? null : request.stepNodeId,
        transitionIndex: request.transitionIndex,
      };
      const transition = this.transitionAt(location);
      const legacyDecisionCount =
        transition?.requiredDecisionKinds?.length ?? 0;
      this.selectedScopeKey.set(this.scopeKeyForLocation(location));
      const row = this.rows.find(
        (candidate) =>
          candidate.scopeCode === location.scopeCode &&
          candidate.stepNodeId === location.stepNodeId &&
          candidate.transitionIndex === location.transitionIndex,
      );
      const locationLabel = this.locationLabel(location);
      let focusEditor = false;
      if (row) {
        this.loadSelection(row);
        this.statusMessage.set(
          this.contextual
            ? null
            : `Showing a decision rule for ${locationLabel}.`,
        );
        focusEditor = true;
      } else if (legacyDecisionCount > 0) {
        this.resetEditor();
        const legacy = this.legacyRows.find(
          (candidate) => candidate.locationKey === this.locationKey(location),
        );
        if (legacy) {
          this.loadLegacySelection(legacy);
        } else {
          this.selectedLegacyLocationKey.set(this.locationKey(location));
        }
        this.statusMessage.set(
          `${locationLabel} has ${legacyDecisionCount} preserved legacy decision ` +
            `requirement${legacyDecisionCount === 1 ? '' : 's'}. ` +
            'They are read-only compatibility evidence; add a structured rule only if a separate requirement is intended.',
        );
      } else if (
        transition &&
        this.canAuthor &&
        !this.interactionBlocked &&
        !this.referenceLoading &&
        this.referenceDataReady
      ) {
        this.startAddDecision(
          location.scopeCode,
          this.locationKey(location),
          true,
          `Add a decision ${this.statusChecksPresentation ? 'check' : 'rule'} for ${locationLabel}. The status change is already selected.`,
        );
        focusEditor = true;
      } else {
        this.resetEditor();
        this.statusMessage.set(
          `${locationLabel} has no structured decision rules. Decision-rule authoring is not available right now.`,
        );
      }
      this.focusAfterRender(() =>
        focusEditor
          ? this.decisionEditorHeading
          : legacyDecisionCount > 0
            ? this.decisionEditorHeading
            : this.decisionSectionHeading,
      );
    });
  }

  focusStatusChecks(request: StatusCheckContext): void {
    const location = this.statusContextLocation(request);
    const ownerAvailable =
      request.scopeCode === 'workflow' ||
      this.readableNodes().filter(
        (node) =>
          node.id === request.stepNodeId && node.typeCode === 'human_step',
      ).length === 1;
    const indexAvailable =
      request.transitionIndex === null ||
      (Number.isInteger(request.transitionIndex) &&
        request.transitionIndex >= 0 &&
        this.transitionAt(location) !== null);
    if (!ownerAvailable || !indexAvailable) {
      this.statusMessage.set(
        'That status-check context is no longer available. No check was created.',
      );
      this.focusAfterRender(() => this.decisionSectionHeading);
      return;
    }
    this.requestDiscardPending(() => {
      this.resetEditor();
      this.statusCheckContext.set({ ...request });
      const location = this.statusContextLocation(request);
      this.selectedScopeKey.set(this.scopeKeyForLocation(location));
      const exactRows = this.visibleRuleRows.filter(
        (row) =>
          (row.structured ?? row.legacy)?.transitionIndex ===
          request.transitionIndex,
      );
      const first =
        exactRows[0] ?? this.visibleRuleRows.find((row) => row.direct);
      if (first?.structured) this.loadSelection(first.structured);
      else if (first?.legacy) this.loadLegacySelection(first.legacy);
      this.focusAfterRender(() =>
        first ? this.decisionEditorHeading : this.decisionRuleListHeading,
      );
    });
  }

  private decisionGateLocation(
    request: WorkflowReadOnlyGraphDecisionGateRequest,
  ): DecisionLocation | null {
    const isTransition = request.origin.locationKind === 'transition';
    if (
      (isTransition && request.origin.transitionIndex === null) ||
      (!isTransition && request.origin.transitionIndex !== null)
    ) {
      return null;
    }

    if (request.ownerSourceNodeId === null) {
      return request.ownerSourceNodeIndex === null
        ? {
            scopeCode: isTransition ? 'workflow_transition' : 'workflow',
            stepNodeId: null,
            transitionIndex: request.origin.transitionIndex,
          }
        : null;
    }

    const nodeIndex = request.ownerSourceNodeIndex;
    const owner =
      nodeIndex === null ? null : (this.document?.nodes[nodeIndex] ?? null);
    if (
      !owner ||
      owner.id !== request.ownerSourceNodeId ||
      owner.typeCode !== 'human_step'
    ) {
      return null;
    }

    return {
      scopeCode: isTransition ? 'step_transition' : 'step',
      stepNodeId: request.ownerSourceNodeId,
      transitionIndex: request.origin.transitionIndex,
    };
  }

  decisionKindLabel(row: WorkflowDraftDecisionRow): string {
    return row.decision
      ? this.humanizeCode(row.decision.decisionKind)
      : 'Invalid rule';
  }

  outcomeLabel(decision: WorkflowDecisionRequirement | null): string {
    return decision
      ? decision.requiredOutcomeCode
        ? this.humanizeCode(decision.requiredOutcomeCode)
        : 'Approved (default)'
      : 'Missing rule details';
  }

  reactionLabel(decision: WorkflowDecisionRequirement | null): string {
    if (!decision) {
      return 'Missing next action';
    }
    const reactionCode = decision.reaction?.reactionCode ?? '';
    const target = decision.reaction?.targetStateCode;
    switch (reactionCode) {
      case '':
      case 'permit':
        return 'Allow requested status change';
      case 'block':
        return 'Always block status change, even with the required result';
      case 'branch':
        return target
          ? `Destination status: ${this.humanizeCode(target)}`
          : 'Use requested status (no override)';
      case 'auto_advance':
        return target
          ? `Destination status: ${this.humanizeCode(target)} (does not advance automatically)`
          : 'Use requested status (does not advance automatically)';
      default:
        return this.humanizeCode(reactionCode);
    }
  }

  eligibilityLabel(decision: WorkflowDecisionRequirement | null): string {
    if (!decision) {
      return 'Invalid';
    }
    if (decision.eligibleRoleCodes != null && !Array.isArray(decision.eligibleRoleCodes)) return 'Saved role guidance is unavailable';
    if (decision.eligibleRoleCodes?.some((code) => code === null)) {
      return 'Invalid eligible-role entry';
    }
    const roles = decision.eligibleRoleCodes?.length
      ? decision.eligibleRoleCodes
          .filter((code): code is string => code !== null)
          .map((code) => this.humanizeCode(code))
          .join(', ')
      : 'No eligible roles';
    return decision.ownerQueueCode
      ? `${roles}; ${this.humanizeCode(decision.ownerQueueCode)} queue`
      : `${roles}; no owning queue`;
  }

  hasInertPersistedTarget(): boolean {
    const reaction = this.selectedRow?.decision?.reaction;
    return Boolean(
      reaction?.targetStateCode &&
      !this.isTargetBearingReaction(reaction.reactionCode),
    );
  }

  decisionAriaLabel(row: WorkflowDraftDecisionRow): string {
    return `Review ${this.decisionKindLabel(row)} decision ${this.statusChecksPresentation ? 'check' : 'rule'} at ${row.locationLabel}`;
  }

  private requestRemoveDecision(): void {
    const selection = this.selectedDecision();
    const row = this.selectedRow;
    if (
      !selection ||
      !row ||
      !this.document ||
      !this.canAuthor ||
      this.interactionBlocked ||
      this.referenceLoading
    ) {
      return;
    }

    const removalContext = () => JSON.stringify([this.document && [this.document.workflowCode, this.document.definitionVersionCode, this.document.packReference],
      this.resetVersion, this.resetContextStepNodeId, this.selectedScopeKey(), this.selectedDecision(),
      this.canAuthor, this.interactionBlocked, this.inspectionMode, this.referenceLoading, this.formDirty(), this.notificationEditorPending,
      this.locationSignature(selection, this.document), this.decisionsAt(selection)]);
    const removalSnapshot = removalContext();
    void this.confirmLocally({
        heading: this.statusChecksPresentation
          ? 'Remove this decision check?'
          : 'Remove this decision rule?',
        message:
          (row.decision?.notifications?.length
            ? `${this.decisionKindLabel(row)} and its ${row.decision.notifications.length} notification${row.decision.notifications.length === 1 ? '' : 's'} will be removed from ${row.locationLabel}.`
            : `${this.decisionKindLabel(row)} will be removed from ${row.locationLabel}.`) +
          (row.decision?.notificationMessage
            ? ' Its notification title, body and context settings will also be removed.'
            : ''),
        confirmText: this.statusChecksPresentation
          ? 'Remove decision check'
          : 'Remove decision rule',
        cancelText: this.statusChecksPresentation
          ? 'Keep decision check'
          : 'Keep decision rule',
      })
      .then((confirmed) => {
        if (this.destroyRef.destroyed) return;
        if (!confirmed) {
          this.focusAfterRender(() => this.decisionEditorHeading);
          return;
        }
        if (removalContext() !== removalSnapshot) {
          this.showLocalError('This decision, attached content or editor context changed. Review it again before removing it.');
          return;
        }

        const decisions = this.decisionsAt(selection);
        const nextDocument = this.replaceDecisions(
          selection,
          decisions.filter((_, index) => index !== selection.decisionIndex),
        );
        this.resetEditor();
        this.statusMessage.set(
          this.statusChecksPresentation
            ? 'Decision check removed from the working draft.'
            : 'Decision rule removed from the working draft.',
        );
        this.documentChange.emit(nextDocument);
        this.focusAfterRender(() =>
          this.contextual
            ? this.decisionRuleListHeading
            : this.decisionSectionHeading,
        );
      });
  }

  private loadSelection(row: WorkflowDraftDecisionRow): void {
    this.inventoryAddEntry = false;
    this.isAddingDecision.set(false);
    this.contextualView.set('summary');
    this.selectedLegacyLocationKey.set(null);
    this.selectedLegacyKey.set(null);
    this.selectedScopeKey.set(this.scopeKeyForLocation(row));
    this.selectedDecision.set({
      scopeCode: row.scopeCode,
      stepNodeId: row.stepNodeId,
      transitionIndex: row.transitionIndex,
      decisionIndex: row.decisionIndex,
      locationSignature: row.locationSignature,
    });
    this.selectedEligibleRolesWereNull =
      row.decision === null || row.decision.eligibleRoleCodes === null;
    this.hasInvalidEligibleRoleEntries.set(
      readableArray(row.decision?.eligibleRoleCodes).some((code) => code === null),
    );
    this.decisionFormValues = this.toFormValues(row, row.decision);
    this.decisionFormConfig = this.buildFormConfig(
      false,
      row.decision ?? undefined,
    );
    this.setFormDirty(false);
    this.localError.set(null);
    const isUnavailableNode =
      (row.scopeCode === 'step' || row.scopeCode === 'step_transition') &&
      !this.isAvailableLocation(row);
    this.statusMessage.set(
      this.hasOpaqueDecisionCollections ? this.opaqueCollectionMessage : row.decision === null
        ? this.inspectionMode
          ? 'This incomplete saved entry remains visible as immutable evidence.'
          : 'This invalid entry can be replaced with a complete rule or removed explicitly.'
        : isUnavailableNode
          ? this.inspectionMode
            ? 'This rule is attached to non-human saved evidence and remains visible for inspection.'
            : 'This rule is attached to non-human draft evidence. It can be inspected or removed, but new rules are limited to the workflow and human steps.'
          : null,
    );
  }

  private loadLegacySelection(row: WorkflowDraftLegacyDecisionRow): void {
    this.inventoryAddEntry = false;
    this.isAddingDecision.set(false);
    this.contextualView.set('summary');
    this.selectedDecision.set(null);
    this.selectedLegacyKey.set(row.key);
    this.selectedLegacyLocationKey.set(row.locationKey);
    this.selectedScopeKey.set(this.scopeKeyForLocation(row));
    this.selectedEligibleRolesWereNull = true;
    this.hasInvalidEligibleRoleEntries.set(false);
    this.decisionFormValues = this.emptyFormValues();
    this.decisionFormConfig = [];
    this.setFormDirty(false);
    this.localError.set(null);
    if (this.hasOpaqueDecisionCollections) this.statusMessage.set(this.opaqueCollectionMessage);
  }

  private resolveSelection(
    selection: DecisionSelection,
  ): WorkflowDraftDecisionRow | null {
    return (
      this.rows.find(
        (row) =>
          row.scopeCode === selection.scopeCode &&
          row.stepNodeId === selection.stepNodeId &&
          row.transitionIndex === selection.transitionIndex &&
          row.decisionIndex === selection.decisionIndex &&
          row.locationSignature === selection.locationSignature,
      ) ?? null
    );
  }

  private appendRows(
    rows: WorkflowDraftDecisionRow[],
    location: DecisionLocation,
    decisions: Array<WorkflowDecisionRequirement | null> | null,
  ): void {
    readableArray(decisions).forEach((decision, decisionIndex) => {
      const locationSignature = this.locationSignature(location);
      rows.push({
        ...location,
        decisionIndex,
        locationSignature,
        key: `${this.locationKey(location)}:decision:${decisionIndex}`,
        scopeLabel: this.scopeLabel(location.scopeCode, location.stepNodeId),
        locationLabel: this.locationLabel(location),
        decision,
      });
    });
  }

  private get transitionLocations(): Array<{
    location: DecisionLocation;
    locationKey: string;
    scopeLabel: string;
    locationLabel: string;
    transition: WorkflowAllowedTransition;
    isAuthorable: boolean;
  }> {
    const document = this.document;
    if (!document) {
      return [];
    }

    const rows: Array<{
      location: DecisionLocation;
      locationKey: string;
      scopeLabel: string;
      locationLabel: string;
      transition: WorkflowAllowedTransition;
      isAuthorable: boolean;
    }> = [];
    readableArray(document.workflowTransitions).forEach(
      (transition, transitionIndex) => {
        if (!transition) {
          return;
        }
        const location: DecisionLocation = {
          scopeCode: 'workflow_transition',
          stepNodeId: null,
          transitionIndex,
        };
        rows.push({
          location,
          locationKey: this.locationKey(location),
          scopeLabel: this.scopeLabel(location.scopeCode),
          locationLabel: this.locationLabel(location),
          transition,
          isAuthorable: true,
        });
      },
    );
    for (const node of this.readableNodes(document)) {
      readableArray(node.allowedTransitions).forEach((transition, transitionIndex) => {
        if (!transition) {
          return;
        }
        const location: DecisionLocation = {
          scopeCode: 'step_transition',
          stepNodeId: node.id,
          transitionIndex,
        };
        rows.push({
          location,
          locationKey: this.locationKey(location),
          scopeLabel: this.scopeLabel(location.scopeCode, node.id),
          locationLabel: this.locationLabel(location),
          transition,
          isAuthorable: node.typeCode === 'human_step',
        });
      });
    }
    return rows;
  }

  private decisionsAt(
    location: DecisionLocation,
    document: WorkflowDefinitionDocument | null = this.document,
  ): Array<WorkflowDecisionRequirement | null> {
    if (!document) {
      return [];
    }
    if (location.scopeCode === 'workflow') {
      return readableArray(document.requiredDecisions);
    }
    if (location.scopeCode === 'step') {
      return readableArray(this.readableNodes(document).find((node) => node.id === location.stepNodeId)?.requiredDecisions);
    }
    return readableArray(this.transitionAt(location, document)?.requiredDecisions);
  }

  private replaceDecisions(
    location: DecisionLocation,
    decisions: Array<WorkflowDecisionRequirement | null>,
  ): WorkflowDefinitionDocument {
    const document = this.document!;
    const nextDecisions =
      decisions.length === 0 &&
      this.initiallyNullCollections.has(this.collectionShapeKey(location))
        ? null
        : decisions;
    if (location.scopeCode === 'workflow') {
      return {
        ...document,
        requiredDecisions: nextDecisions,
      };
    }
    if (location.scopeCode === 'step') {
      return {
        ...document,
        nodes: document.nodes.map((node) =>
          node.id === location.stepNodeId
            ? { ...node, requiredDecisions: nextDecisions }
            : node,
        ),
      };
    }
    if (location.scopeCode === 'workflow_transition') {
      return {
        ...document,
        workflowTransitions: (document.workflowTransitions ?? []).map(
          (transition, index) =>
            index === location.transitionIndex && transition
              ? { ...transition, requiredDecisions: nextDecisions }
              : transition,
        ),
      };
    }
    return {
      ...document,
      nodes: document.nodes.map((node) =>
        node.id === location.stepNodeId
          ? {
              ...node,
              allowedTransitions: (node.allowedTransitions ?? []).map(
                (transition, index) =>
                  index === location.transitionIndex && transition
                    ? { ...transition, requiredDecisions: nextDecisions }
                    : transition,
              ),
            }
          : node,
      ),
    };
  }

  private transitionAt(
    location: DecisionLocation,
    document: WorkflowDefinitionDocument | null = this.document,
  ): WorkflowAllowedTransition | null {
    if (!document || location.transitionIndex === null) {
      return null;
    }
    if (location.scopeCode === 'workflow_transition') {
      return readableArray(document.workflowTransitions)[location.transitionIndex] ?? null;
    }
    if (location.scopeCode === 'step_transition') {
      return readableArray(this.readableNodes(document).find((node) => node.id === location.stepNodeId)?.allowedTransitions)[location.transitionIndex] ?? null;
    }
    return null;
  }

  private buildScopeGroup(
    key: string,
    kind: WorkflowDraftDecisionScopeGroup['kind'],
    stepNodeId: string | null,
    label: string,
    authorable: boolean,
  ): WorkflowDraftDecisionScopeGroup {
    return {
      key,
      kind,
      stepNodeId,
      label,
      structuredCount: this.rows.filter(
        (row) => this.scopeKeyForLocation(row) === key,
      ).length,
      legacyCount: this.legacyRows.filter(
        (row) => this.scopeKeyForLocation(row) === key,
      ).length,
      authorable,
    };
  }

  private structuredNavigatorRow(
    row: WorkflowDraftDecisionRow,
    direct: boolean,
  ): WorkflowDraftDecisionNavigatorRow {
    return {
      key: row.key,
      kind: 'structured',
      scopeKey: this.scopeKeyForLocation(row),
      direct,
      locationLabel: row.locationLabel,
      structured: row,
      legacy: null,
    };
  }

  private scopeKeyForNode(nodeId: string | null): string {
    if (!nodeId) {
      return 'evidence';
    }
    return this.humanStepNodes.some((node) => node.id === nodeId)
      ? `step:${nodeId}`
      : 'evidence';
  }

  private scopeKeyForLocation(
    location: Pick<DecisionLocation, 'scopeCode' | 'stepNodeId'>,
  ): string {
    return location.scopeCode === 'workflow' ||
      location.scopeCode === 'workflow_transition'
      ? 'workflow'
      : this.scopeKeyForNode(location.stepNodeId);
  }

  private authorableLocationsForScope(
    group: WorkflowDraftDecisionScopeGroup,
  ): DecisionLocation[] {
    if (!group.authorable) {
      return [];
    }
    if (group.kind === 'workflow') {
      return [
        { scopeCode: 'workflow', stepNodeId: null, transitionIndex: null },
        ...this.transitionLocations
          .filter((row) => row.location.scopeCode === 'workflow_transition')
          .map((row) => row.location),
      ];
    }
    if (group.kind === 'step' && group.stepNodeId) {
      return [
        {
          scopeCode: 'step',
          stepNodeId: group.stepNodeId,
          transitionIndex: null,
        },
        ...this.transitionLocations
          .filter(
            (row) =>
              row.location.scopeCode === 'step_transition' &&
              row.location.stepNodeId === group.stepNodeId &&
              row.isAuthorable,
          )
          .map((row) => row.location),
      ];
    }
    return [];
  }

  private initializeDefaultSelection(): void {
    const valid = this.rows.find((row) => row.decision !== null);
    if (valid) {
      this.selectedScopeKey.set(this.scopeKeyForLocation(valid));
      this.loadSelection(valid);
      return;
    }
    const invalid = this.rows[0];
    if (invalid) {
      this.selectedScopeKey.set(this.scopeKeyForLocation(invalid));
      this.loadSelection(invalid);
      return;
    }
    const legacy = this.legacyRows[0];
    if (legacy) {
      this.loadLegacySelection(legacy);
    }
  }

  private initializeResetSelection(): void {
    if (this.resetContextStepNodeId === undefined) {
      this.initializeDefaultSelection();
      return;
    }

    this.selectedScopeKey.set(
      this.resetContextStepNodeId === null
        ? 'workflow'
        : this.scopeKeyForNode(this.resetContextStepNodeId),
    );
    if (this.statusChecksPresentation)
      this.statusCheckContext.set({
        scopeCode: this.resetContextStepNodeId === null ? 'workflow' : 'step',
        stepNodeId: this.resetContextStepNodeId,
        transitionIndex: null,
      });
    this.initializeSelectionForCurrentScope();
  }

  private initializeSelectionForCurrentScope(): void {
    const groups = this.scopeGroups;
    if (!groups.some((group) => group.key === this.selectedScopeKey())) {
      this.selectedScopeKey.set(groups[0]?.key ?? 'workflow');
    }
    const visible = this.visibleRuleRows;
    const valid = visible.find(
      (row) => row.structured?.decision !== null && row.structured !== null,
    );
    if (valid?.structured) {
      this.loadSelection(valid.structured);
      return;
    }
    const invalid = visible.find((row) => row.structured !== null);
    if (invalid?.structured) {
      this.loadSelection(invalid.structured);
      return;
    }
    const legacy = visible.find((row) => row.legacy !== null);
    if (legacy?.legacy) {
      this.loadLegacySelection(legacy.legacy);
    }
  }

  private parseAnyLocation(locationKey: string): DecisionLocation | null {
    if (locationKey === 'workflow') {
      return { scopeCode: 'workflow', stepNodeId: null, transitionIndex: null };
    }
    if (locationKey.startsWith('step:')) {
      const nodeId = locationKey.slice('step:'.length);
      return nodeId
        ? { scopeCode: 'step', stepNodeId: nodeId, transitionIndex: null }
        : null;
    }
    return (
      this.transitionLocations.find((row) => row.locationKey === locationKey)
        ?.location ?? null
    );
  }

  private currentFormLocation(): DecisionLocation | null {
    return this.isAddingDecision()
      ? this.parseAnyLocation(this.decisionFormValues.locationKey)
      : this.selectedDecision();
  }

  private targetStateCodesForLocation(location: DecisionLocation): string[] {
    const active = new Set(this.stateCodes);
    if (
      location.scopeCode === 'workflow_transition' ||
      location.scopeCode === 'step_transition'
    ) {
      const selectedTransition = this.transitionAt(location);
      if (!selectedTransition) {
        return [];
      }
      const peers =
        location.scopeCode === 'workflow_transition'
          ? readableArray(this.document?.workflowTransitions)
          : readableArray(this.readableNodes().find((node) => node.id === location.stepNodeId)?.allowedTransitions);
      const available = new Set(
        peers
          .filter(
            (transition): transition is WorkflowAllowedTransition =>
              transition !== null &&
              transition.fromStateCode === selectedTransition.fromStateCode &&
              transition.toStateCode !== selectedTransition.fromStateCode &&
              active.has(transition.toStateCode),
          )
          .map((transition) => transition.toStateCode),
      );
      return this.stateCodes.filter((code) => available.has(code));
    }

    const transitions =
      location.scopeCode === 'workflow'
        ? readableArray(this.document?.workflowTransitions)
        : readableArray(this.readableNodes().find((node) => node.id === location.stepNodeId)?.allowedTransitions);
    const destinationsBySource = new Map<string, Set<string>>();
    for (const transition of transitions) {
      if (!transition) {
        continue;
      }
      const destinations =
        destinationsBySource.get(transition.fromStateCode) ?? new Set<string>();
      if (
        transition.toStateCode !== transition.fromStateCode &&
        active.has(transition.toStateCode)
      ) {
        destinations.add(transition.toStateCode);
      }
      destinationsBySource.set(transition.fromStateCode, destinations);
    }
    const sourceDestinations = [...destinationsBySource.values()];
    if (sourceDestinations.length === 0) {
      return [];
    }
    return this.stateCodes.filter(
      (code) =>
        !destinationsBySource.has(code) &&
        sourceDestinations.every((destinations) => destinations.has(code)),
    );
  }

  private decisionReactionOptionLabel(code: string): string {
    switch (code) {
      case 'permit':
        return 'Allow requested status change';
      case 'block':
        return 'Always block status change (even with the required result)';
      case 'branch':
        return 'Optionally redirect to another status';
      case 'auto_advance':
        return 'Optionally redirect to another status (does not advance automatically)';
      default:
        return this.humanizeCode(code);
    }
  }

  private reactionOptions(
    existingDecision?: WorkflowDecisionRequirement,
  ): InputSelectOption[] {
    const currentCode = existingDecision?.reaction?.reactionCode;
    const options: InputSelectOption[] = [
      { value: '', viewValue: 'Allow requested status change (default)' },
    ];
    const codes = this.decisionReactionCodes.filter(
      (code) => !this.graphWorkflow || !this.isTargetBearingReaction(code),
    );
    if (currentCode && !codes.includes(currentCode)) {
      codes.push(currentCode);
    }
    return [
      ...options,
      ...codes.map((code) => ({
        value: code,
        viewValue:
          this.graphWorkflow && this.isTargetBearingReaction(code)
            ? `${this.decisionReactionOptionLabel(code)} (saved; unsupported in graph workflows)`
            : this.decisionReactionCodes.includes(code)
              ? this.decisionReactionOptionLabel(code)
              : `${this.decisionReactionOptionLabel(code)} (not active)`,
      })),
    ];
  }

  private destinationOptions(
    existingDecision?: WorkflowDecisionRequirement,
  ): InputSelectOption[] {
    const availableCodes = this.graphWorkflow
      ? []
      : this.availableTargetStateCodes;
    const currentCode = existingDecision?.reaction?.targetStateCode;
    const codes = [...availableCodes];
    if (currentCode && !codes.includes(currentCode)) {
      codes.push(currentCode);
    }
    return [
      { value: '', viewValue: 'No destination override' },
      ...codes.map((code) => {
        const suffix = availableCodes.includes(code)
          ? ''
          : this.stateCodes.includes(code)
            ? ' (not available here)'
            : ' (not active)';
        return {
          value: code,
          viewValue: `${this.humanizeCode(code)}${suffix}`,
        };
      }),
    ];
  }

  private buildFormConfig(
    isNew: boolean,
    existingDecision?: WorkflowDecisionRequirement,
  ): InputConfig[] {
    const reactionCode = this.decisionFormValues.reactionCode;
    const preserveExistingInertTarget = Boolean(
      existingDecision?.reaction?.targetStateCode &&
      existingDecision.reaction.reactionCode === reactionCode,
    );
    const locationOptions = this.selectedScopeGroup
      ? this.authorableLocationsForScope(this.selectedScopeGroup).map(
          (location) => ({
            value: this.locationKey(location),
            viewValue: this.locationLabel(location),
          }),
        )
      : [];
    const locationInput: InputConfig = {
      name: 'locationKey',
      type: EzFormInputType.SELECT,
      label: 'Apply to',
      row: 1,
      width: 'fill',
      options: locationOptions,
      validation: [Validators.required],
      disabled: !isNew,
    };
    return [
      ...(isNew || !this.contextual ? [locationInput] : []),
      {
        name: 'decisionKind',
        type: EzFormInputType.SELECT,
        label: this.statusChecksPresentation
          ? 'Recorded decision type'
          : this.contextual
            ? 'Required decision'
            : 'Decision type',
        row: 2,
        width: 'fill',
        options: this.codeOptions(
          this.decisionKindCodes,
          existingDecision ? [existingDecision.decisionKind] : [],
        ),
        validation: [Validators.required],
      },
      {
        name: 'requiredOutcomeCode',
        type: EzFormInputType.SELECT,
        label: this.statusChecksPresentation
          ? 'Required recorded result'
          : 'Required result',
        row: 2,
        width: 'fill',
        options: [
          { value: '', viewValue: this.inventoryAddEntry ? 'Choose a required recorded result' : 'Approved (default)' },
          ...this.codeOptions(
            this.decisionOutcomeCodes,
            existingDecision?.requiredOutcomeCode
              ? [existingDecision.requiredOutcomeCode]
              : [],
          ),
        ],
        ...(this.inventoryAddEntry ? { validation: [Validators.required] } : {}),
      },
      {
        name: 'reactionCode',
        type: EzFormInputType.SELECT,
        label: 'Status change effect',
        row: 3,
        width: 'fill',
        options: this.reactionOptions(existingDecision),
      },
      ...(!this.graphWorkflow || existingDecision?.reaction?.targetStateCode
        ? [
            {
              name: 'targetStateCode',
              type: EzFormInputType.SELECT,
              label: this.graphWorkflow
                ? 'Saved destination status'
                : this.contextual
                  ? 'Optional destination status'
                  : 'Destination status',
              row: 4,
              width: 'fill',
              options: this.destinationOptions(existingDecision),
              disabled: () =>
                (this.graphWorkflow &&
                  this.isTargetBearingReaction(
                    this.decisionFormValues.reactionCode,
                  )) ||
                (!this.isTargetBearingReaction(
                  this.decisionFormValues.reactionCode,
                ) &&
                  !preserveExistingInertTarget),
            } as InputConfig,
          ]
        : []),
      {
        name: 'eligibleRoleCodes',
        type: EzFormInputType.MULTI_SELECT,
        label: this.contextual
          ? 'Role guidance (does not grant access)'
          : 'Eligible roles',
        row: 5,
        width: 'fill',
        options: this.codeOptions(
          this.roleCodes,
          readableArray(existingDecision?.eligibleRoleCodes),
        ),
      },
      {
        name: 'ownerQueueCode',
        type: EzFormInputType.SELECT,
        label: this.contextual
          ? 'Queue guidance (does not assign work)'
          : 'Owning queue',
        row: 6,
        width: 'fill',
        options: [
          { value: '', viewValue: 'No owner queue' },
          ...this.codeOptions(
            this.queueCodes,
            existingDecision?.ownerQueueCode
              ? [existingDecision.ownerQueueCode]
              : [],
          ),
        ],
      },
    ];
  }

  private codeOptions(
    activeCodes: string[],
    currentCodes: Array<string | null>,
  ): InputSelectOption[] {
    const codes = [...activeCodes];
    for (const code of currentCodes) {
      if (code === null) {
        continue;
      }
      if (code && !codes.includes(code)) {
        codes.push(code);
      }
    }
    return codes.map((code) => ({
      value: code,
      viewValue: activeCodes.includes(code)
        ? this.humanizeCode(code)
        : `${this.humanizeCode(code)} (not active)`,
    }));
  }

  private parseLocation(
    scopeCode: DecisionScopeCode,
    locationKey: string,
  ): DecisionLocation | null {
    if (scopeCode === 'workflow') {
      return {
        scopeCode,
        stepNodeId: null,
        transitionIndex: null,
      };
    }
    if (scopeCode === 'step') {
      const nodeId = locationKey.startsWith('step:')
        ? locationKey.slice('step:'.length)
        : '';
      return nodeId
        ? { scopeCode, stepNodeId: nodeId, transitionIndex: null }
        : null;
    }
    const matchingTransition = this.transitionLocations.find(
      (row) =>
        row.location.scopeCode === scopeCode && row.locationKey === locationKey,
    );
    return matchingTransition?.location ?? null;
  }

  private isAvailableLocation(location: DecisionLocation): boolean {
    if (location.scopeCode === 'workflow') {
      return true;
    }
    if (location.scopeCode === 'step') {
      return this.humanStepNodes.some(
        (node) => node.id === location.stepNodeId,
      );
    }
    if (location.scopeCode === 'step_transition') {
      return (
        this.humanStepNodes.some((node) => node.id === location.stepNodeId) &&
        this.transitionAt(location) !== null
      );
    }
    return this.transitionAt(location) !== null;
  }

  private readableNodes(document: WorkflowDefinitionDocument | null = this.document): WorkflowDefinitionNode[] {
    return readableArray(document?.nodes).filter(node => savedObject(node) && typeof node.id === 'string');
  }

  private get humanStepNodes(): WorkflowDefinitionNode[] {
    return this.readableNodes()
      .filter((node) => node.typeCode === 'human_step')
      .sort(
        (left, right) =>
          left.displayOrder - right.displayOrder ||
          left.id.localeCompare(right.id),
      );
  }

  private locationKey(location: DecisionLocation): string {
    switch (location.scopeCode) {
      case 'workflow':
        return 'workflow';
      case 'step':
        return `step:${location.stepNodeId ?? ''}`;
      case 'workflow_transition':
        return `workflow-transition:${location.transitionIndex ?? ''}`;
      case 'step_transition':
        return `step-transition:${location.stepNodeId ?? ''}:${location.transitionIndex ?? ''}`;
    }
  }

  private locationSignature(
    location: DecisionLocation,
    document: WorkflowDefinitionDocument | null = this.document,
  ): string {
    const transition = this.transitionAt(location, document);
    return transition
      ? `${this.locationKey(location)}:${transition.fromStateCode}:${transition.toStateCode}`
      : this.locationKey(location);
  }

  private collectionShapeKey(
    location: DecisionLocation,
    document: WorkflowDefinitionDocument | null = this.document,
  ): string {
    const transition = this.transitionAt(location, document);
    return transition
      ? [
          location.scopeCode,
          location.stepNodeId ?? '',
          transition.fromStateCode,
          transition.toStateCode,
        ].join(':')
      : this.locationKey(location);
  }

  private scopeLabel(
    scopeCode: DecisionScopeCode,
    stepNodeId: string | null = null,
  ): string {
    const node = stepNodeId
      ? this.readableNodes().find((candidate) => candidate.id === stepNodeId)
      : null;
    switch (scopeCode) {
      case 'workflow':
        return 'Whole workflow';
      case 'step':
        return node && node.typeCode !== 'human_step'
          ? this.inspectionMode
            ? 'Other saved evidence'
            : 'Other draft evidence'
          : 'Work step';
      case 'workflow_transition':
        return 'Workflow status change';
      case 'step_transition':
        return node && node.typeCode !== 'human_step'
          ? this.inspectionMode
            ? 'Other saved evidence'
            : 'Other draft evidence'
          : 'Work-step status change';
    }
  }

  private locationLabel(location: DecisionLocation): string {
    if (location.scopeCode === 'workflow') {
      return 'Every workflow status change';
    }
    const node = this.readableNodes().find(
      (candidate) => candidate.id === location.stepNodeId,
    );
    if (location.scopeCode === 'step') {
      return node
        ? `Every ${this.nodeFriendlyName(node)} status change`
        : this.inspectionMode
          ? 'Every status change for a missing saved element'
          : 'Every status change for a missing draft element';
    }
    const transition = this.transitionAt(location);
    const transitionLabel = transition
      ? `${this.humanizeCode(transition.fromStateCode)} → ` +
        `${this.humanizeCode(transition.toStateCode)}`
      : 'missing status change';
    if (
      location.scopeCode === 'step_transition' &&
      node?.typeCode !== 'human_step'
    ) {
      const nodeName = node
        ? this.nodeFriendlyName(node)
        : this.inspectionMode
          ? 'Missing saved element'
          : 'Missing draft element';
      return `${nodeName} · Only ${transitionLabel}`;
    }
    return `Only ${transitionLabel}`;
  }

  private nodeFriendlyName(node: WorkflowDefinitionNode): string {
    return (
      node.displayName?.trim() ||
      (node.stepCode ? this.humanizeCode(node.stepCode) : '') ||
      this.humanizeCode(node.id)
    );
  }

  humanizeCode(code: string | null | undefined): string {
    const normalized = code
      ?.trim()
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/[_\-.]+/g, ' ')
      .replace(/\s+/g, ' ');
    if (!normalized) {
      return 'Not set';
    }
    return normalized
      .split(' ')
      .map((word) =>
        word.length > 1
          ? `${word[0].toUpperCase()}${word.slice(1).toLowerCase()}`
          : word.toUpperCase(),
      )
      .join(' ');
  }

  private toFormValues(
    location: DecisionLocation,
    decision: WorkflowDecisionRequirement | null,
  ): DecisionFormValues {
    return {
      scopeCode: location.scopeCode,
      locationKey: this.locationKey(location),
      decisionKind: decision
        ? (decision.decisionKind ?? '')
        : (this.decisionKindCodes[0] ?? ''),
      requiredOutcomeCode: decision?.requiredOutcomeCode ?? '',
      reactionCode: decision?.reaction?.reactionCode ?? '',
      targetStateCode: decision?.reaction?.targetStateCode ?? '',
      eligibleRoleCodes: readableArray(decision?.eligibleRoleCodes).filter(
        (code): code is string => code !== null,
      ),
      ownerQueueCode: decision?.ownerQueueCode ?? '',
    };
  }

  private rebuildFormConfig(): void {
    const existing = this.selectedRow?.decision ?? undefined;
    if (this.selectedDecision() || this.isAddingDecision()) {
      this.decisionFormConfig = this.buildFormConfig(
        this.isAddingDecision(),
        existing ?? undefined,
      );
    }
  }

  private migrateNullTransitionCollectionShapes(
    previousDocument: WorkflowDefinitionDocument | null,
    currentDocument: WorkflowDefinitionDocument | null,
  ): void {
    if (
      !previousDocument ||
      !currentDocument ||
      this.initiallyNullCollections.size === 0
    ) {
      return;
    }

    const previousLocations =
      this.transitionCollectionLocations(previousDocument);
    const currentLocations =
      this.transitionCollectionLocations(currentDocument);
    const previousShapeKeys = new Set(
      previousLocations.map((location) =>
        this.collectionShapeKey(location, previousDocument),
      ),
    );
    const currentShapeKeys = new Set(
      currentLocations.map((location) =>
        this.collectionShapeKey(location, currentDocument),
      ),
    );

    for (const previousLocation of previousLocations) {
      const previousShapeKey = this.collectionShapeKey(
        previousLocation,
        previousDocument,
      );
      if (
        !this.initiallyNullCollections.has(previousShapeKey) ||
        currentShapeKeys.has(previousShapeKey)
      ) {
        continue;
      }

      this.initiallyNullCollections.delete(previousShapeKey);
      const currentAtSameOrdinal = currentLocations.find(
        (location) =>
          this.locationKey(location) === this.locationKey(previousLocation),
      );
      if (!currentAtSameOrdinal) {
        continue;
      }

      const currentShapeKey = this.collectionShapeKey(
        currentAtSameOrdinal,
        currentDocument,
      );
      if (!previousShapeKeys.has(currentShapeKey)) {
        this.initiallyNullCollections.add(currentShapeKey);
      }
    }
  }

  private transitionCollectionLocations(
    document: WorkflowDefinitionDocument,
  ): DecisionLocation[] {
    const locations: DecisionLocation[] = [];
    for (const node of this.readableNodes(document)) {
      readableArray(node.allowedTransitions).forEach((transition, transitionIndex) => {
        if (transition) {
          locations.push({
            scopeCode: 'step_transition',
            stepNodeId: node.id,
            transitionIndex,
          });
        }
      });
    }
    readableArray(document.workflowTransitions).forEach(
      (transition, transitionIndex) => {
        if (transition) {
          locations.push({
            scopeCode: 'workflow_transition',
            stepNodeId: null,
            transitionIndex,
          });
        }
      },
    );
    return locations;
  }

  private captureInitialCollectionShapes(): void {
    this.initiallyNullCollections.clear();
    this.captureNewNullCollectionShapes();
  }

  private captureNewNullCollectionShapes(): void {
    const document = this.document;
    if (!document) {
      return;
    }
    const locations: DecisionLocation[] = [
      { scopeCode: 'workflow', stepNodeId: null, transitionIndex: null },
    ];
    for (const node of this.readableNodes(document)) {
      locations.push({
        scopeCode: 'step',
        stepNodeId: node.id,
        transitionIndex: null,
      });
      readableArray(node.allowedTransitions).forEach((transition, transitionIndex) => {
        if (transition) {
          locations.push({
            scopeCode: 'step_transition',
            stepNodeId: node.id,
            transitionIndex,
          });
        }
      });
    }
    readableArray(document.workflowTransitions).forEach(
      (transition, transitionIndex) => {
        if (transition) {
          locations.push({
            scopeCode: 'workflow_transition',
            stepNodeId: null,
            transitionIndex,
          });
        }
      },
    );

    for (const location of locations) {
      if (this.rawDecisionCollection(location) === null) {
        this.initiallyNullCollections.add(this.collectionShapeKey(location));
      }
    }
  }

  private rawDecisionCollection(
    location: DecisionLocation,
  ): Array<WorkflowDecisionRequirement | null> | null {
    const document = this.document;
    if (!document) {
      return null;
    }
    if (location.scopeCode === 'workflow') {
      return document.requiredDecisions;
    }
    if (location.scopeCode === 'step') {
      return (
        this.readableNodes(document).find((node) => node.id === location.stepNodeId)
          ?.requiredDecisions ?? null
      );
    }
    return this.transitionAt(location)?.requiredDecisions ?? null;
  }

  private requestDiscardPending(
    action: () => void,
    cancelled: () => void = () => undefined,
  ): void {
    if (this.destroyRef.destroyed || this.localConfirmationInProgress || this.ownerViews.focusSuspended()) { cancelled(); return; }
    if (this.notificationEditorPending) {
      this.focusPendingChanges();
      cancelled();
      return;
    }
    if (!this.formDirty()) {
      action();
      return;
    }
    if (this.contextual) {
      if (this.applyPendingChanges()) action();
      else {
        this.focusPendingChanges();
        cancelled();
      }
      return;
    }

    const scopes = this.pendingEditorScopes();
    const context = () => JSON.stringify([this.document, this.resetVersion, this.selectedDecision(), this.selectedScopeKey(), this.isAddingDecision(), this.canAuthor, this.interactionBlocked,
      scopes.map(scope => scope.state() === 'clean' ? [scope.id, 'clean'] : [scope.id, scope.state(), scope.fingerprint()]),
      this.pendingEditorScopes().filter(scope => scope.state() !== 'clean').map(scope => [scope.id, scope.state(), scope.fingerprint()])]);
    const before = context();
    void this.confirmLocally({
        heading: this.statusChecksPresentation
          ? 'Discard unapplied decision check changes?'
          : 'Discard unapplied decision rule changes?',
        message:
          'The current decision form values will be lost. Already-applied draft edits will remain.',
        confirmText: 'Discard decision changes',
        cancelText: 'Keep editing',
      })
      .then((confirmed) => {
        if (this.destroyRef.destroyed) return;
        if (confirmed) {
          if (context() !== before) {
            this.showLocalError('The decision input or context changed while confirmation was open. Review the current input before trying again.');
            cancelled();
            return;
          }
          this.discardPendingChanges();
          if (!this.formDirty() && !this.notificationEditorPending) action();
          else { this.focusPendingChanges(); cancelled(); }
        } else {
          this.focusPendingChanges();
          cancelled();
        }
      });
  }

  private restoreDecisionFormFromDocument(): void {
    if (this.isAddingDecision()) {
      this.resetEditor();
      this.initializeSelectionForCurrentScope();
      return;
    }

    const row = this.selectedRow;
    if (row) {
      this.loadSelection(row);
      return;
    }

    this.resetEditor();
    this.initializeSelectionForCurrentScope();
  }

  private startAddDecision(
    scopeCode: DecisionScopeCode,
    locationKey: string,
    formDirty: boolean,
    statusMessage: string,
    inventoryEntry = false,
  ): void {
    this.inventoryAddEntry = inventoryEntry;
    const location = this.parseLocation(scopeCode, locationKey);
    this.isAddingDecision.set(true);
    this.contextualView.set('rule');
    this.selectedLegacyLocationKey.set(null);
    this.selectedLegacyKey.set(null);
    this.selectedDecision.set(null);
    if (location) {
      this.selectedScopeKey.set(this.scopeKeyForLocation(location));
    }
    this.selectedEligibleRolesWereNull = true;
    this.hasInvalidEligibleRoleEntries.set(false);
    this.decisionFormValues = {
      ...this.emptyFormValues(),
      scopeCode,
      locationKey,
      decisionKind: this.contextual || inventoryEntry ? '' : (this.decisionKindCodes[0] ?? ''),
    };
    this.decisionFormConfig = this.buildFormConfig(true);
    this.setFormDirty(formDirty);
    this.localError.set(null);
    this.statusMessage.set(statusMessage);
  }

  private resetEditor(resetScope = false): void {
    this.inventoryAddEntry = false;
    this.isAddingDecision.set(false);
    this.contextualView.set('summary');
    this.selectedLegacyLocationKey.set(null);
    this.selectedLegacyKey.set(null);
    this.selectedDecision.set(null);
    if (resetScope) {
      this.statusCheckContext.set(null);
      this.selectedScopeKey.set('workflow');
    }
    this.selectedEligibleRolesWereNull = true;
    this.hasInvalidEligibleRoleEntries.set(false);
    this.decisionFormValues = this.emptyFormValues();
    this.decisionFormConfig = [];
    this.setFormDirty(false);
    this.onNotificationPendingChangesChange(false);
    this.localError.set(null);
    this.statusMessage.set(null);
  }

  private setFormDirty(value: boolean): void {
    if (this.formDirty() === value) {
      return;
    }
    this.pendingFormBaseline = value ? this.pendingFormSource(this.document) : null;
    this.formDirty.set(value);
    this.pendingChangesChange.emit(value || this.notificationFormDirty);
  }

  private ensureFormValid(): boolean {
    const formGroup = this.decisionForm?.theFormGroup;
    if (formGroup?.valid || (formGroup?.disabled && this.pendingFormState(this.document) === 'valid')) {
      return true;
    }
    formGroup?.markAllAsTouched();
    this.showLocalError(
      this.statusChecksPresentation
        ? 'Complete the required decision check fields before applying changes.'
        : 'Complete the required decision rule fields before applying changes.',
    );
    return false;
  }

  private showLocalError(message: string): void {
    this.localError.set(message);
    this.focusAfterRender(() => this.decisionError);
  }

  private isTargetBearingReaction(
    reactionCode: string | null | undefined,
  ): boolean {
    return reactionCode
      ? TARGET_BEARING_REACTION_CODES.has(reactionCode)
      : false;
  }

  private emptyFormValues(): DecisionFormValues {
    return {
      scopeCode: 'workflow',
      locationKey: '',
      decisionKind: '',
      requiredOutcomeCode: '',
      reactionCode: '',
      targetStateCode: '',
      eligibleRoleCodes: [],
      ownerQueueCode: '',
    };
  }

  private confirmLocally(request: Parameters<WorkflowDraftConfirmationService['confirm']>[0]): Promise<boolean> {
    if (this.destroyRef.destroyed || this.localConfirmationInProgress || this.ownerViews.focusSuspended()) return Promise.resolve(false);
    this.localConfirmationInProgress = true;
    const resumeFocus = this.ownerViews.suspendFocus();
    const host = this.host.nativeElement, wasInert = host.inert;
    host.inert = true;
    const release = () => {
      this.localConfirmationInProgress = false;
      host.inert = wasInert;
      resumeFocus();
    };
    try {
      const result = this.confirmation.confirm(request);
      void result.then(release, release);
      return result;
    } catch (error) { release(); throw error; }
  }

  private focusAfterRender(
    target: () => ElementRef<HTMLElement> | undefined,
  ): void {
    setTimeout(() => {
      const element = target()?.nativeElement;
      if (this.destroyRef.destroyed || this.ownerViews.focusSuspended() || element?.closest('[hidden],[inert]')) return;
      element?.focus();
    });
  }
}

interface PersistedTransitionSelector {
  index: number | null;
  fromStateCode: string | null;
  toStateCode: string | null;
}

interface PersistedDecisionSelector {
  index: number | null;
  decisionKind: string | null;
  occurrence: number;
}

function persistedTransitionSelector(
  path: string,
): PersistedTransitionSelector | null {
  const indexedMatch =
    /(?:workflowTransitions|allowedTransitions)\[(\d+)\]/.exec(path);
  if (indexedMatch) {
    return {
      index: Number.parseInt(indexedMatch[1], 10),
      fromStateCode: null,
      toStateCode: null,
    };
  }

  const transitionMatch = /\[transition=([^\]]+)\]/.exec(path);
  const transitionId = transitionMatch?.[1] ?? '';
  const separatorIndex = transitionId.indexOf('->');
  if (separatorIndex < 0) {
    return null;
  }
  return {
    index: null,
    fromStateCode: transitionId.slice(0, separatorIndex),
    toStateCode: transitionId.slice(separatorIndex + 2),
  };
}

function persistedDecisionSelector(
  path: string,
): PersistedDecisionSelector | null {
  const indexedMatch = /requiredDecisions\[(\d+)\]/.exec(path);
  if (indexedMatch) {
    return {
      index: Number.parseInt(indexedMatch[1], 10),
      decisionKind: null,
      occurrence: 1,
    };
  }

  const decisionMatch =
    /requiredDecisions\[kind=([^,\]]+)(?:,occurrence=(\d+))?\]/.exec(path);
  if (!decisionMatch) {
    return null;
  }
  return {
    index: null,
    decisionKind: decisionMatch[1],
    occurrence: decisionMatch[2] ? Number.parseInt(decisionMatch[2], 10) : 1,
  };
}

function stepNodeIdFromPersistedDecisionPath(path: string): string | null {
  return /^\$\.nodes\[id=([^\]]+)\]/.exec(path)?.[1] ?? null;
}

function normalizeOptional(value: string | null | undefined): string | null {
  const normalized = value?.trim();
  return normalized ? normalized : null;
}
